import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import PasswordInput from '../components/common/PasswordInput';
import styled, { keyframes, css } from 'styled-components';
import { formatTimestamp, saveToLocalStorage, loadFromLocalStorage } from '../utils/helpers';
import { STORAGE_KEYS } from '../constants';
import { Link, useSearchParams } from 'react-router-dom';
import GlobalFooter from '../components/GlobalFooter';
import SeoHead from '../components/SeoHead';
import db from '../utils/firestore';
import { doc, onSnapshot, getDoc, getDocFromServer } from 'firebase/firestore';
import TokenRateTile from '../components/TokenRateTile';
import UsageLimitsCard from '../components/wallet/UsageLimitsCard';
import WalletSpendMeters from '../components/wallet/WalletSpendMeters';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { requestBrandedVerificationEmail } from '../lib/identity/signInOrLink';
import { AnonymousAuthService } from '../utils/anonymousAuth';
import { getUserWalletEntry, saveUserWalletEntry, UserWalletEntry, removeSavedCard, removeSavedBankAccount } from '../repositories/walletRepository';
import { loadAccountProfile } from '../services/prest0nRuntime/accountResolver';
import { possessiveName } from '../utils/possessiveName';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { stripeApi, ConnectedAccountData } from '../services/stripeClient';
import { stripeHealthChecker } from '../services/stripeHealthCheck';
import VisaIcon from '../icons/VisaIcon';
import MastercardIcon from '../icons/MastercardIcon';
// UsageHistory intentionally not rendered — see the note at the section list
// (its backing collection has no writer; removed from the page 2026-08-02).
import { EarningsHistory } from '../components/EarningsHistory';
import { ExpensesHistory } from '../components/ExpensesHistory';
import { TransactionsHistory } from '../components/TransactionsHistory';
import { WalletDataProvider, useMaybeWalletUsage, useMaybeWalletSummary } from '../context/WalletDataContext';
import { useAuth } from '../context/AuthContext';
import { formatUsd } from '../components/common/CopyableAmount/utils';
import { usdToWu, formatWu, WU_LABEL, STANDARD_RATE_USD_PER_WU, MONTHLY_WU_PER_PRESTON } from '../config/workUnits';
import Pr0Unit from '../components/common/Pr0Unit';
import { useLowBalanceWarning } from '../components/GlobalChatSystem/useLowBalanceWarning';
import { useTrialStatus } from '../hooks/useTrialStatus';
import { useTopUp } from '../context/TopUpModalContext';
import { useUsageVisibility } from '../hooks/useUsageVisibility';
import { CardOrgSharing } from '../components/wallet/CardOrgSharing';

// Add TypeScript interfaces for payment methods
interface PaymentMethod {
  id: string;
  type: 'card' | 'bank';
  displayName: string;
  data: any;
}

interface SavedCard {
  id: string;
  cardNumber: string;
  expiryDate: string;
  cardholderName: string;
  cardType: string;
  isDefault: boolean;
}

interface SavedBankAccount {
  id: string;
  bankName: string;
  accountHolderName: string;
  accountNumber: string;
  routingNumber: string;
  accountType: string;
  bankAddress?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  isDefault: boolean;
}

/* Main container - mobile-first approach */
const WalletContainer = styled.main`
  padding: 1.5rem 1rem;
  width: 100%;
  margin: 0 auto;
  min-height: 100vh;
  overflow-x: hidden;
  background: var(--brand-canvas);

  /* Tablet (768px) */
  @media (min-width: 768px) {
    padding: 2rem 1.5rem;
  }

  /* Laptop (1024px) */
  @media (min-width: 1024px) {
    max-width: 900px;
    padding: 2.5rem;
    min-height: calc(100vh - 160px);
  }

  /* Desktop (1200px) */
  @media (min-width: 1200px) {
    max-width: 1000px;
  }
`;

const WalletHeader = styled.h1`
  font-family: 'Space Grotesk', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 2rem; /* Smaller on mobile */
  color: var(--brand-text);
  margin-bottom: 1.5rem;
  font-weight: 400;
  letter-spacing: -0.02em;
  text-transform: lowercase;

  /* Increase size on larger screens */
  @media (min-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 2rem;
  }

  @media (min-width: 1024px) {
    margin-bottom: 2.5rem;
  }
`;

// ORG-USAGE-2026: pointer to the dedicated Activity page, which replaced the
// in-wallet usage analytics block.
const ActivityPointer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;

  p {
    margin: 0;
    color: #666;
    font-size: 0.9375rem;
    line-height: 1.6;
  }
`;

const ActivityLink = styled(Link)`
  display: inline-block;
  color: var(--brand-gold);
  font-size: 0.9375rem;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const WalletSection = styled.section`
  background: white;
  border-radius: 1rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 0.375rem 1.25rem rgba(0,0,0,0.04);
  border: 1px solid var(--brand-line);
  transition: all 0.3s ease;

  @media (min-width: 768px) {
    padding: 2rem;
    margin-bottom: 2rem;
    border-radius: 1.25rem;
  }

  @media (min-width: 1024px) {
    padding: 2.5rem;
    border-radius: 1rem;

    &:hover {
      box-shadow: 0 0.5rem 1.875rem rgba(0,0,0,0.06);
      transform: translateY(-2px);
    }
  }
`;

const SectionTitle = styled.h2`
  font-family: 'Space Grotesk', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 1.5rem;
  color: var(--brand-text);
  margin-bottom: 1rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  text-transform: lowercase;

  @media (min-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 1.5rem;
  }
`;

const SectionContent = styled.div`
  color: var(--brand-text-secondary);
  line-height: 1.8;
  font-size: 1rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;

  p {
    margin-bottom: 1.5rem;
    text-transform: lowercase;
  }

  @media (min-width: 768px) {
    font-size: 1.1rem;
  }
`;

// ============================================================================
// Banner & Stats Styled Components
// ============================================================================

const NotificationBanner = styled.div<{ variant: 'warning' | 'info' }>`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border-radius: 0.75rem;
  margin-bottom: 1rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.875rem;
  line-height: 1.5;
  /* R18 (owner directive 2026-08-09): banners ride the Prest0 palette — the
     old 'info' blue (#EBF4FF/#93C5FD) was off-palette chrome. Warning keeps
     the stronger gold; red/green stay reserved for spend semantics. Tokens:
     src/components/common/PortalBanner.tsx PRESTO_PALETTE. */
  background: ${p => p.variant === 'warning' ? '#FFF8E6' : '#FBF8F0'};
  border: 1px solid ${p => p.variant === 'warning' ? 'var(--brand-gold)' : '#E8DFC4'};
  color: ${p => p.variant === 'warning' ? '#7A5C00' : '#1a1a2e'};

  @media (min-width: 768px) {
    padding: 1rem 1.25rem;
    font-size: 0.9375rem;
  }
`;

const BannerIcon = styled.span`
  font-size: 1.1rem;
  flex-shrink: 0;
  margin-top: 0.05rem;
`;

const BannerBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const BannerTitle = styled.strong`
  font-weight: 600;
  text-transform: lowercase;
`;

const BannerActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 0.25rem;
`;

const BannerButton = styled.button`
  background: none;
  border: 1px solid currentColor;
  border-radius: 0.4rem;
  padding: 0.2rem 0.6rem;
  font-size: 0.8rem;
  cursor: pointer;
  color: inherit;
  font-family: inherit;
  text-transform: lowercase;
  transition: opacity 0.15s;
  &:hover { opacity: 0.7; }
`;

const BannerDismiss = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: inherit;
  opacity: 0.5;
  font-size: 1rem;
  padding: 0;
  line-height: 1;
  flex-shrink: 0;
  &:hover { opacity: 1; }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-bottom: 1rem;

  @media (min-width: 600px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 1rem;
  }
`;

const StatCard = styled.div`
  background: white;
  border: 1px solid var(--brand-line);
  border-radius: 0.75rem;
  padding: 0.875rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const StatLabel = styled.p`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.75rem;
  color: #888;
  margin: 0;
  text-transform: lowercase;
`;

const StatValue = styled.p`
  font-family: 'Space Grotesk', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--brand-text);
  margin: 0;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 1.5rem 0;
  
  li {
    position: relative;
    padding-left: 1.5rem;
    margin-bottom: 0.75rem;
    text-transform: lowercase;
    
    &:before {
      content: "•";
      position: absolute;
      left: 0.5rem;
      color: var(--brand-gold);
      font-weight: bold;
    }
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const ComingSoonBadge = styled.span`
  background: linear-gradient(135deg, var(--brand-gold) 0%, var(--brand-gold-hover) 100%);
  color: white;
  padding: 0.35rem 0.75rem;
  border-radius: 30px;
  font-size: 0.7rem;
  text-transform: lowercase;
  letter-spacing: 0.05em;
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(198, 167, 94, 0.2);
  margin-left: 0.75rem;
  transform: translateY(-1px);
`;

const ScreenReaderOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
`;

const FooterWrapper = styled.div`
  width: 100%;
  margin-top: auto;
`;

// Add new styled components for balance section
const BalanceCard = styled.div`
  background: linear-gradient(135deg, var(--brand-black) 0%, var(--brand-charcoal) 100%);
  border-radius: 1rem;
  padding: 2rem;
  margin-bottom: 2rem;
  color: white;
  box-shadow: 0 0.5rem 2rem rgba(26, 26, 26, 0.3);
  border-bottom: 2px solid var(--brand-gold);

  @media (min-width: 768px) {
    padding: 2.5rem;
    border-radius: 1.25rem;
  }
`;

const BalanceHeader = styled.h2`
  font-family: 'Space Grotesk', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  font-weight: 500;
  text-transform: lowercase;

  @media (min-width: 768px) {
    font-size: 1.75rem;
  }
`;

const BalanceRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const BalanceLabel = styled.span`
  font-size: 1rem;
  opacity: 0.9;
  text-transform: lowercase;
  
  @media (min-width: 768px) {
    font-size: 1.1rem;
  }
`;

const BalanceValue = styled.span`
  font-size: 1.5rem;
  font-weight: 600;

  @media (min-width: 768px) {
    font-size: 1.75rem;
  }
`;

// Shimmer animation for loading states
const balanceShimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

// Skeleton shimmer for balance values while loading
const BalanceShimmer = styled.span`
  display: inline-block;
  width: 120px;
  height: 1.75rem;
  background: linear-gradient(90deg,
    rgba(255,255,255,0.1) 0%,
    rgba(255,255,255,0.3) 50%,
    rgba(255,255,255,0.1) 100%
  );
  background-size: 200% 100%;
  animation: ${balanceShimmer} 1.5s infinite;
  border-radius: 0.375rem;
  vertical-align: middle;
`;

const TransactionSection = styled.div`
  margin-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  padding-top: 1.5rem;
`;

const TransactionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  cursor: pointer;
  user-select: none;
  
  &:hover {
    opacity: 0.8;
  }
`;

const TransactionTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 500;
  text-transform: lowercase;
  margin: 0;
`;

const ExpandIcon = styled.span<{ expanded: boolean }>`
  font-size: 1.5rem;
  transition: transform 0.3s ease;
  transform: ${props => props.expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const TransactionList = styled.div<{ expanded: boolean }>`
  max-height: ${props => props.expanded ? '300px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

const NoTransactions = styled.div`
  padding: 1rem;
  text-align: center;
  opacity: 0.7;
  font-style: italic;
  text-transform: lowercase;
`;

// Add styled components for payment section
// Token rate section styles
const TokenRateCard = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 1.25rem 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 0.5rem 2rem rgba(0, 0, 0, 0.08);
  border: 1px solid var(--brand-line);
  border-top: 2px solid var(--brand-gold);
`;

const TokenRateHeader = styled.h3`
  font-family: 'Space Grotesk', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 1.25rem;
  color: var(--brand-text);
  margin: 0 0 0.75rem 0;
  font-weight: 500;
  text-transform: lowercase;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

// LiveBadge + blink removed 2026-08-13: the purchase modal's conversion line
// shows the static Standard Rate ($2.18 / WU) — nothing "live" to badge.

const CurrencySuffix = styled.span`
  color: var(--brand-text-secondary);
  font-size: 0.95rem;
  margin-left: 0.25rem;
  text-transform: lowercase;
`;

// Removed help text per user request

const RateRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
`;

const RateLabel = styled.span`
  color: var(--brand-text-secondary);
  text-transform: lowercase;
`;

const RateValue = styled.span`
  font-size: 1.4rem;
  font-weight: 600;
  color: var(--brand-text);
`;

const PaymentCard = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 0.5rem 2rem rgba(0, 0, 0, 0.08);
  border: 1px solid var(--brand-line);
  transition: all 0.3s ease;

  @media (min-width: 768px) {
    padding: 2.5rem;
    border-radius: 1.25rem;
  }

  @media (min-width: 1024px) {
    &:hover {
      box-shadow: 0 0.75rem 2.5rem rgba(0, 0, 0, 0.12);
      transform: translateY(-2px);
    }
  }
`;

const PaymentHeader = styled.h2`
  font-family: 'Space Grotesk', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 1.5rem;
  color: var(--brand-text);
  margin-bottom: 1.5rem;
  font-weight: 500;
  text-transform: lowercase;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  @media (min-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 2rem;
  }
`;

const DropdownHeader = styled.button<{ $isExpanded: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-bottom: ${props => props.$isExpanded ? '1.5rem' : '0'};
  transition: margin-bottom 0.3s ease;

  &:hover {
    opacity: 0.8;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid var(--brand-gold);
    outline-offset: 4px;
    border-radius: 4px;
  }
`;

const DropdownHeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-family: 'Space Grotesk', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 1.25rem;
  color: var(--brand-text);
  font-weight: 600;
  text-transform: lowercase;

  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

const DropdownChevron = styled.span<{ $isExpanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => props.$isExpanded ? '#f0f0f0' : 'transparent'};
  transition: all 0.3s ease;

  svg {
    width: 20px;
    height: 20px;
    stroke: #666;
    transition: transform 0.3s ease;
    transform: rotate(${props => props.$isExpanded ? '180deg' : '0deg'});
  }

  &:hover {
    background: #f0f0f0;
  }
`;

const DropdownContent = styled.div<{ $isExpanded: boolean }>`
  overflow: hidden;
  max-height: ${props => props.$isExpanded ? '2000px' : '0'};
  opacity: ${props => props.$isExpanded ? '1' : '0'};
  transition: max-height 0.4s ease, opacity 0.3s ease;
  padding: ${props => props.$isExpanded ? '1.5rem 0 0.5rem' : '0'};
`;

const SecurityBadge = styled.span`
  background: linear-gradient(135deg, var(--brand-gold) 0%, var(--brand-gold-hover) 100%);
  color: white;
  padding: 0.25rem 0.6rem;
  border-radius: 20px;
  font-size: 0.7rem;
  text-transform: lowercase;
  letter-spacing: 0.05em;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.25rem;

  svg {
    width: 12px;
    height: 12px;
  }
`;

const PaymentMethodGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-top: 1rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--brand-line);

  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
  }
`;

const PaymentMethodCard = styled.div<{ isSelected: boolean; disabled?: boolean }>`
  border: 2px solid ${props => props.isSelected ? 'var(--brand-gold)' : 'var(--brand-line)'};
  border-radius: 12px;
  padding: 1.5rem;
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  transition: all 0.3s ease;
  background: ${props => props.isSelected ? 'rgba(198, 167, 94, 0.03)' : 'white'};
  position: relative;
  opacity: ${props => props.disabled ? 0.7 : 1};
  filter: ${props => props.disabled ? 'saturate(0.85)' : 'none'};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;

  &:hover {
    border-color: ${props => props.disabled ? (props.isSelected ? 'var(--brand-gold)' : 'var(--brand-line)') : 'var(--brand-gold)'};
    box-shadow: ${props => props.disabled ? 'none' : '0 4px 12px rgba(198, 167, 94, 0.1)'};
  }

  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const PaymentMethodHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
`;

const PaymentMethodTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--brand-text);
  margin: 0;
  text-transform: lowercase;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Space Grotesk', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;

  svg {
    width: 20px;
    height: 20px;
    color: var(--brand-gold);
  }
`;

const PaymentMethodDescription = styled.p`
  color: var(--brand-text-secondary);
  font-size: 0.9rem;
  margin: 0;
  text-transform: lowercase;
  line-height: 1.5;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const AddNewButton = styled.button`
  background: linear-gradient(135deg, var(--brand-gold) 0%, var(--brand-gold-hover) 100%);
  color: var(--brand-text);
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-transform: lowercase;
  width: auto;
  justify-content: center;
  margin-top: 1rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;

  &:hover {
    background: linear-gradient(135deg, var(--brand-gold-hover) 0%, #A07D3A 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(198, 167, 94, 0.3);
  }

  svg {
    width: 16px;
    height: 16px;
  }

  &:disabled {
    background: linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%);
    color: #6b7280;
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }
`;

const SavedCardsSection = styled.div`
  margin-bottom: 2rem;

  &:first-child {
    margin-top: 0;
  }
`;

const PaymentSectionLabel = styled.h3`
  font-family: 'Space Grotesk', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 1rem;
  color: var(--brand-text-secondary);
  margin: 0 0 1rem 0;
  font-weight: 600;
  text-transform: lowercase;
`;

const SavedCardsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const SavedCardItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border: 1px solid var(--brand-line);
  border-radius: 12px;
  background: var(--brand-canvas);
  transition: all 0.3s ease;

  &:hover {
    background: #f5f5f5;
    border-color: var(--brand-gold);
  }
`;

const CardInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
`;

const CardIcon = styled.div<{ cardType?: string }>`
  width: 48px;
  height: 30px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: lowercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  background: ${props => {
    const brand = (props.cardType || '').toLowerCase();
    if (brand.includes('visa') || brand.includes('master')) return '#ffffff';
    if (brand.includes('visa')) return 'linear-gradient(135deg, #1a73e8 0%, #4285f4 100%)';
    if (brand.includes('master')) return 'linear-gradient(135deg, #eb001b 0%, #ff5f00 100%)';
    if (brand.includes('amex') || brand.includes('american')) return 'linear-gradient(135deg, #016fd0 0%, #2e77d0 100%)';
    if (brand.includes('discover')) return 'linear-gradient(135deg, #ff6000 0%, #ff8c42 100%)';
    if (brand.includes('diners')) return 'linear-gradient(135deg, #006272 0%, #0088a3 100%)';
    if (brand.includes('jcb')) return 'linear-gradient(135deg, #0b4ea2 0%, #1976d2 100%)';
    return 'linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)';
  }};
  border: ${props => {
    const brand = (props.cardType || '').toLowerCase();
    if (brand.includes('visa') || brand.includes('master')) return '1px solid #e5e7eb';
    return 'none';
  }};
`;

const CardBrandText = styled.span<{ cardType?: string }>`
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.3px;
  ${props => {
    const brand = (props.cardType || '').toLowerCase();
    if (brand.includes('visa')) return 'font-family: "Arial Black", Arial, sans-serif;';
    if (brand.includes('master')) return 'font-family: "Arial", sans-serif; font-weight: 800;';
    if (brand.includes('amex') || brand.includes('american')) return 'font-family: "Arial", sans-serif; font-weight: 700;';
    return 'font-family: inherit;';
  }}
`;

const CardDetails = styled.div`
  flex: 1;
`;

const CardNumber = styled.div`
  font-weight: 600;
  color: var(--brand-text);
  font-size: 0.95rem;
`;

const CardExpiry = styled.div`
  color: #666;
  font-size: 0.8rem;
  text-transform: lowercase;
`;

const CardholderName = styled.div`
  color: #4b5563;
  font-size: 0.85rem;
  margin-top: 4px;
  text-transform: lowercase;
  font-weight: 500;
`;

const CardActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const CardActionButton = styled.button`
  background: none;
  border: 1px solid var(--brand-line);
  border-radius: 6px;
  padding: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--brand-text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f5f5f5;
    border-color: var(--brand-gold);
    color: var(--brand-gold);
  }

  &.delete:hover {
    background: #ffebee;
    border-color: #f44336;
    color: #f44336;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const modalFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const modalSlideUp = keyframes`
  from { transform: translateY(8px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const AddCardModal = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 20001;
  padding: 1rem;
  overflow-y: auto;
  animation: ${props => props.isOpen ? css`${modalFadeIn} 0.18s ease` : 'none'};
  /* Prefer visual stability across mobile chrome UI bars */
  @supports (height: 100dvh) {
    min-height: 100dvh;
  }

  /* Small phone screens and landscape phones: anchor near top for readability */
  @media (max-width: 480px) {
    align-items: flex-start;
    padding-top: calc(env(safe-area-inset-top, 0px) + 1rem);
    padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 1rem);
  }

  @media (max-height: 600px) and (orientation: landscape) {
    align-items: flex-start;
    padding-top: 1rem;
    padding-bottom: 1rem;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  width: 100%;
  max-width: clamp(320px, 92vw, 640px);
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0,0,0,0.12);
  animation: ${modalSlideUp} 0.22s ease;

  @supports (height: 100dvh) {
    max-height: calc(100dvh - 2rem);
  }
  
  @media (min-width: 768px) {
    padding: 2.5rem;
  }

  @media (max-width: 480px) {
    padding: 1.25rem;
    border-radius: 12px;
    @supports (height: 100dvh) {
      max-height: calc(100dvh - 2.5rem);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const ModalTitle = styled.h3`
  font-family: 'Space Grotesk', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--brand-text);
  margin: 0;
  text-transform: lowercase;
`;

const ModalSubtitle = styled.p`
  margin: 0.25rem 0 1.25rem 0;
  color: var(--brand-text-secondary);
  font-size: 0.95rem;
  line-height: 1.5;
  text-transform: lowercase;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const ProgressStep = styled.div`
  margin: 1rem 0;
  padding: 0.75rem 1rem;
  background: #FAF8F0;
  border: 1px solid #E8DFC4;
  border-radius: 8px;
  color: #8B7333;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::before {
    content: '⏳';
    font-size: 1rem;
  }
`;

const ErrorBox = styled.div`
  margin: 1rem 0;
  padding: 1rem;
  background: #fee2e2;
  border: 1px solid #fca5a5;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ErrorTitle = styled.div`
  font-weight: 600;
  color: #dc2626;
  font-size: 0.9rem;
`;

const ErrorMessage = styled.div`
  color: #991b1b;
  font-size: 0.85rem;
  line-height: 1.4;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: #f5f5f5;
    color: #333;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ConfirmModal = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 20002;
  padding: 1rem;
  animation: ${props => props.isOpen ? css`${modalFadeIn} 0.18s ease` : 'none'};
`;

const ConfirmModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.15);
  animation: ${modalSlideUp} 0.22s ease;
  text-align: center;

  @media (max-width: 480px) {
    padding: 1.5rem;
    margin: 0 1rem;
  }
`;

const ConfirmIconWrapper = styled.div<{ variant?: 'danger' | 'warning' }>`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${props => props.variant === 'danger' ? '#FEE2E2' : '#FEF3C7'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.25rem;

  svg {
    width: 28px;
    height: 28px;
    stroke: ${props => props.variant === 'danger' ? '#DC2626' : '#D97706'};
  }
`;

const ConfirmTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--brand-text);
  margin: 0 0 0.75rem;
  text-transform: lowercase;
`;

const ConfirmMessage = styled.p`
  font-size: 0.95rem;
  color: #666;
  margin: 0 0 1.5rem;
  line-height: 1.5;
  text-transform: lowercase;
`;

const ConfirmActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: center;

  @media (max-width: 380px) {
    flex-direction: column-reverse;
  }
`;

const ConfirmButton = styled.button<{ variant?: 'danger' | 'cancel' }>`
  flex: 1;
  max-width: 160px;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: lowercase;

  ${props => props.variant === 'danger' ? css`
    background: #DC2626;
    color: white;
    border: none;

    &:hover {
      background: #B91C1C;
    }

    &:active {
      transform: scale(0.98);
    }
  ` : css`
    background: white;
    color: #333;
    border: 2px solid #e0e0e0;

    &:hover {
      background: #f5f5f5;
      border-color: #ccc;
    }

    &:active {
      transform: scale(0.98);
    }
  `}

  @media (max-width: 380px) {
    max-width: none;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FormLabel = styled.label`
  display: block;
  font-weight: 600;
  color: var(--brand-text);
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  text-transform: lowercase;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid var(--brand-line);
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: white;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;

  &:focus {
    outline: none;
    border-color: var(--brand-gold);
    box-shadow: 0 0 0 3px rgba(198, 167, 94, 0.12);
  }

  &::placeholder {
    color: #999;
    text-transform: lowercase;
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid var(--brand-line);
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: white;
  cursor: pointer;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;

  &:focus {
    outline: none;
    border-color: var(--brand-gold);
    box-shadow: 0 0 0 3px rgba(198, 167, 94, 0.12);
  }

  option {
    padding: 0.5rem;
    text-transform: lowercase;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }

  /* Prevent content from stretching too wide on ultra-wide screens */
  @media (min-width: 1400px) {
    grid-template-columns: repeat(2, minmax(0, 480px));
    justify-content: center;
  }
`;

const HelperText = styled.p`
  margin: 0.4rem 0 0 0;
  font-size: 0.8rem;
  color: #6b7280;
  text-transform: lowercase;
`;

const ErrorText = styled.p`
  margin: 0.4rem 0 0 0;
  font-size: 0.8rem;
  color: #b91c1c;
  text-transform: lowercase;
`;

const SecurityInfo = styled.div`
  background: var(--brand-canvas);
  border-radius: 8px;
  padding: 1rem;
  margin: 1.5rem 0;
  border-left: 4px solid var(--brand-gold);
`;

const SecurityInfoText = styled.p`
  color: var(--brand-text-secondary);
  font-size: 0.85rem;
  margin: 0;
  line-height: 1.5;
  text-transform: lowercase;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    width: 16px;
    height: 16px;
    color: var(--brand-gold);
    margin-top: 0;
    flex-shrink: 0;
  }
`;

const PoweredByStripe = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.75rem;
  color: #6b7280;
  text-transform: lowercase;
  margin-top: 0;
`;

const StripeField = styled.div`
  padding: 12px 10px;
  border: 2px solid var(--brand-line);
  border-radius: 8px;
  background: var(--brand-canvas);
  transition: all 0.2s ease;
  position: relative;
  min-height: 52px;
  overflow: hidden;

  &:hover {
    border-color: #d1d5db;
  }

  &:focus-within {
    background: #fff;
    border-color: var(--brand-gold);
    box-shadow: 0 0 0 3px rgba(198, 167, 94, 0.12);
  }
`;

const SavingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(255,255,255,0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  z-index: 3;
`;

const ModalProgressBar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 3px;
  width: 100%;
  background: linear-gradient(90deg, #DDD4B8, var(--brand-gold), #DDD4B8);
  background-size: 200% 100%;
  animation: progressSlide 1.2s linear infinite;
  @keyframes progressSlide { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
`;

const FieldOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  background: linear-gradient(180deg, rgba(255,255,255,0.96), rgba(255,255,255,0.9));
  z-index: 2;
`;

const Spinner = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #DDD4B8;
  border-top-color: transparent;
  animation: spin 0.8s linear infinite;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const SkeletonStack = styled.div`
  display: grid;
  grid-auto-rows: 10px;
  row-gap: 8px;
  flex: 1;
`;

const SkeletonLine = styled.div`
  height: 10px;
  width: 100%;
  border-radius: 6px;
  background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s ease-in-out infinite;
  @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
`;

const AcceptedBrands = styled.div`
  margin-top: 0.5rem;
  font-size: 0.8rem;
  color: #6b7280;
  text-transform: lowercase;
`;

const OptionCard = styled.div`
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1rem;
  background: #fafafa;
`;

const OptionTitle = styled.h4`
  margin: 0 0 0.25rem 0;
  font-size: 1rem;
  color: #111827;
  font-weight: 600;
  text-transform: lowercase;
`;

const OptionDescription = styled.p`
  margin: 0 0 0.75rem 0;
  font-size: 0.9rem;
  color: #6b7280;
  text-transform: lowercase;
`;

const InlineBadge = styled.span`
  display: inline-block;
  margin-left: 0.5rem;
  background: #FAF8F0;
  color: #8B7333;
  border: 1px solid #E8DFC4;
  border-radius: 999px;
  padding: 0.1rem 0.5rem;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: lowercase;
`;

const CollapsibleToggle = styled.button`
  background: none;
  border: none;
  color: var(--brand-gold);
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  text-transform: lowercase;
  &:hover { text-decoration: underline; }
`;

const CollapsibleContent = styled.div<{ open: boolean }>`
  max-height: ${props => props.open ? '2000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  
  @media (max-width: 480px) {
    flex-direction: column-reverse;
  }
`;

const ModalButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: lowercase;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;

  ${props => props.variant === 'primary' ? css`
    background: linear-gradient(135deg, var(--brand-gold) 0%, var(--brand-gold-hover) 100%);
    color: var(--brand-text);
    border: none;

    &:hover {
      background: linear-gradient(135deg, var(--brand-gold-hover) 0%, #A07D3A 100%);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(198, 167, 94, 0.3);
    }
  ` : css`
    background: white;
    color: var(--brand-text-secondary);
    border: 2px solid var(--brand-line);

    &:hover {
      background: #f5f5f5;
      border-color: #ccc;
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const BankAccountSection = styled.div`
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e0e0e0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #666;
  
  svg {
    width: 48px;
    height: 48px;
    color: #ccc;
    margin-bottom: 1rem;
  }
  
  h4 {
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
    color: #333;
    text-transform: lowercase;
  }
  
  p {
    font-size: 0.9rem;
    text-transform: lowercase;
    line-height: 1.5;
  }
`;

// Add styled components for purchase/deposit functionality
const BalanceActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
  flex-wrap: wrap;
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const ActionButton = styled.button`
  background: linear-gradient(135deg, var(--brand-gold) 0%, var(--brand-gold-hover) 100%);
  color: var(--brand-text);
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-transform: lowercase;
  min-width: 150px;
  justify-content: center;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;

  &:hover {
    background: linear-gradient(135deg, var(--brand-gold-hover) 0%, #A07D3A 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(198, 167, 94, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  svg {
    width: 16px;
    height: 16px;
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const SecondaryActionButton = styled(ActionButton)`
  background: white;
  color: var(--brand-gold);
  border: 2px solid var(--brand-gold);

  &:hover {
    background: var(--brand-canvas);
    border-color: var(--brand-gold-hover);
    color: var(--brand-gold-hover);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(198, 167, 94, 0.2);
  }
`;

const PurchaseModeToggle = styled.div`
  display: flex;
  background: #f5f5f5;
  border-radius: 8px;
  padding: 0.25rem;
  margin-bottom: 1.5rem;
`;

const ModeButton = styled.button<{ active: boolean }>`
  flex: 1;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: lowercase;
  background: ${props => props.active ? 'white' : 'transparent'};
  color: ${props => props.active ? 'var(--brand-gold)' : 'var(--brand-text-secondary)'};
  box-shadow: ${props => props.active ? '0 2px 4px rgba(0,0,0,0.1)' : 'none'};

  &:hover {
    color: var(--brand-gold);
  }
`;

const ConversionInfo = styled.div`
  background: var(--brand-canvas);
  border-radius: 8px;
  padding: 1rem;
  margin: 1rem 0;
  border-left: 4px solid var(--brand-gold);
`;

const ConversionText = styled.p`
  color: var(--brand-text-secondary);
  font-size: 0.9rem;
  margin: 0;
  text-transform: lowercase;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    width: 16px;
    height: 16px;
    color: var(--brand-gold);
  }

  strong {
    color: var(--brand-text);
  }
`;

// Add styled components for payment warning modal
const WarningContainer = styled.div`
  padding: 1rem 0;
`;

const WarningAlert = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 8px;
`;

const WarningIcon = styled.svg`
  width: 24px;
  height: 24px;
  color: #856404;
  flex-shrink: 0;
`;

const WarningContent = styled.div`
  flex: 1;
`;

const WarningText = styled.p`
  margin: 0;
  color: #856404;
  font-weight: 600;
  font-size: 0.9rem;
  text-transform: lowercase;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  
  &:first-child {
    margin-bottom: 0.5rem;
  }
  
  &:last-child {
    font-size: 0.85rem;
    font-weight: 400;
    margin: 0.5rem 0 0 0;
  }
`;

const SuccessAlert = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 1rem;
  background: #F5F0E1;
  border: 1px solid #DDD4B8;
  border-radius: 8px;
`;

const SuccessIcon = styled.svg`
  width: 24px;
  height: 24px;
  color: #8B7333;
  flex-shrink: 0;
`;

const SuccessText = styled.p`
  margin: 0;
  color: #8B7333;
  font-weight: 500;
  font-size: 0.95rem;
  text-transform: lowercase;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  line-height: 1.5;
`;

const ErrorAlert = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 1rem;
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 8px;
`;

const ErrorIcon = styled.svg`
  width: 24px;
  height: 24px;
  color: #721c24;
  flex-shrink: 0;
`;

const NotificationErrorText = styled.p`
  margin: 0;
  color: #721c24;
  font-weight: 500;
  font-size: 0.95rem;
  text-transform: lowercase;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  line-height: 1.5;
`;

const InfoAlert = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 1rem;
  background: #FAF8F0;
  border: 1px solid #E8DFC4;
  border-radius: 8px;
`;

const InfoIcon = styled.svg`
  width: 24px;
  height: 24px;
  color: #8B7333;
  flex-shrink: 0;
`;

const InfoText = styled.p`
  margin: 0;
  color: #8B7333;
  font-weight: 500;
  font-size: 0.95rem;
  text-transform: lowercase;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  line-height: 1.5;
`;

const PaymentMethodsSection = styled.div`
  margin-bottom: 1rem;
`;

const PaymentMethodsTitle = styled.h4`
  margin: 0 0 1rem 0;
  color: var(--brand-text);
  font-size: 1rem;
  text-transform: lowercase;
  font-family: 'Space Grotesk', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 500;
`;

const PaymentMethodsList = styled.div`
  display: flex;
  gap: 1rem;
  flex-direction: column;
`;

const PaymentMethodOption = styled.div`
  padding: 0.75rem;
  border: 1px solid var(--brand-line);
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--brand-gold);
    background: rgba(198, 167, 94, 0.03);
  }
`;

const PaymentMethodIcon = styled.svg`
  width: 20px;
  height: 20px;
  color: var(--brand-gold);
`;

const PaymentMethodName = styled.span`
  font-size: 0.9rem;
  color: var(--brand-text);
  text-transform: lowercase;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

// Inner component that uses wallet context hooks (must be inside WalletDataProvider)
const WalletContent: React.FC = () => {
  // Get real-time wallet summary (balance, totals) from Firestore
  // Balance updates automatically via atomic transactions in walletRepository
  const walletSummary = useMaybeWalletSummary();

  const { user } = useAuth();

  // Derive balance from wallet summary (real-time from Firestore)
  // OLV balance is the single source of truth - stored in Firestore
  // ACCOUNT WALLET DISPLAY (2026-07-13): while acting in an account, the
  // context summary carries the ACCOUNT's paying wallet — the number that
  // actually moves when the account chats — per the owner's "display" decision.
  const olvBalance = walletSummary?.summary?.olvBalance ?? 0;
  // USD balance is DERIVED from olvBalance * exchange_rate (not stored)
  // The context already derives this, but we re-derive here as a safety net
  // in case tokenPriceUsd has a more recent value than the context
  const usdBalance = walletSummary?.summary?.usdBalance ?? 0;
  // Track loading state to show shimmer instead of $0.00 while loading
  const isBalanceLoading = walletSummary?.isLoading ?? true;
  // Whether the displayed balance is the acting ACCOUNT's wallet (labeling only).
  const isAccountWalletDisplayed = walletSummary?.displayScope === 'account';
  // (The former personal-balance mirror lived here for the org-transfer
  // element, removed 2026-08-21 — money-movement surfaces read
  // walletSummary.personalSummary directly where they need it.)

  // Legacy: sequence OLV limit (kept for backwards compatibility)
  const [sequenceOlvLimit, setSequenceOlvLimit] = useState<number | null>(null);

  // Centralized auth state - resolves once on mount via onAuthStateChanged.
  // ProtectedRoute handles gating/redirect; this just waits for the Firebase user object.
  const [authUser, setAuthUser] = useState<ReturnType<typeof getAuth>['currentUser']>(null);
  const authCheckRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    // Single auth check that all dependent effects will wait for
    if (authCheckRef.current) return; // Already checking

    const auth = getAuth();
    const immediateUser = auth.currentUser;

    // Fast path: if already authenticated, set immediately
    if (immediateUser && !immediateUser.isAnonymous) {
      setAuthUser(immediateUser);
      return;
    }

    // Slow path: wait for Firebase auth to settle via onAuthStateChanged.
    // ProtectedRoute already handles gating/redirect — this just waits for the
    // Firebase user object so wallet queries can use it.
    authCheckRef.current = new Promise<void>((resolve) => {
      const unsub = onAuthStateChanged(auth, (user) => {
        if (user && !user.isAnonymous) {
          setAuthUser(user);
          unsub();
          resolve();
        }
      });
    });
  }, []);

  // Get wallet usage context (for displaying usage history)
  const walletUsageContext = useMaybeWalletUsage();

  // Low balance warning state
  const lowBalanceState = useLowBalanceWarning();
  const [lowBalanceDismissed, setLowBalanceDismissed] = useState(false);

  // Trial state — expired trials cannot buy tokens; active trials get a
  // heads-up that the trial (and its credit) has an end date.
  const trialStatus = useTrialStatus();
  // THE top-up popup (2026-08-14 unification): every wallet entry point opens
  // the shared TopUpModal — same popup as the banners and chat.
  const topUp = useTopUp();

  // Org wallet title (2026-08-21 owner directive): resolve the displayed
  // workspace's real name so the balance card reads "[org name]'s wallet
  // balance". Fail-quiet — the generic label stands until (or if) the
  // profile resolves.
  const displayAccountIdForTitle = walletSummary?.displayAccountId ?? null;
  const [orgWalletName, setOrgWalletName] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setOrgWalletName(null);
    if (!displayAccountIdForTitle) return undefined;
    loadAccountProfile(displayAccountIdForTitle)
      .then((profile) => {
        if (cancelled) return;
        const name = (profile?.firmName || profile?.displayName || '').trim();
        setOrgWalletName(name || null);
      })
      .catch(() => { /* generic title stands */ });
    return () => { cancelled = true; };
  }, [displayAccountIdForTitle]);
  // showTrialBlockedModal removed (2026-08-18 audit): never set true — the
  // shared TopUpModal owns the trial-ended notice.

  // Email verification banner state
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationSending, setVerificationSending] = useState(false);
  const [emailBannerDismissed, setEmailBannerDismissed] = useState(() => {
    try { return localStorage.getItem('emailBannerDismissed') === '1'; } catch { return false; }
  });

  // Workspace-owner visibility — gates the owner-only money controls
  // (UsageLimitsCard below). Fail-closed while RBAC resolves.
  const { isOwner: viewerIsWorkspaceOwner, loading: rbacLoading } = useUsageVisibility();

  const handleResendVerification = async () => {
    if (verificationSending || verificationSent) return;
    setVerificationSending(true);
    try {
      const currentUser = getAuth().currentUser;
      if (currentUser) {
        // Branded verification email — the shared helper keeps the raw
        // sendEmailVerification as ITS internal fallback (never rejects).
        await requestBrandedVerificationEmail(currentUser);
        setVerificationSent(true);
      }
    } catch (e) {
      console.warn('resend verification failed:', e);
    } finally {
      setVerificationSending(false);
    }
  };

  // Note: Balance updates are now handled automatically via wallet/summary Firestore document
  // When expenses/earnings/usage are created, the balance is updated atomically
  // No manual localStorage or transaction tracking needed

  // Legacy placeholder for usage tracking (kept for compatibility but not used for balance)
  const _unused_legacy = useCallback(() => {
    // Balance is now managed by WalletSummaryContext
    // This is kept to avoid breaking any code that referenced these variables
  }, []);
  void _unused_legacy; // Suppress unused warning

  // Token price state
  const [tokenPriceUsd, setTokenPriceUsd] = useState<number | null>(null);
  const [tokenPriceLoading, setTokenPriceLoading] = useState<boolean>(true);
  const [tokenPriceError, setTokenPriceError] = useState<string | null>(null);

  // Live token price listener: global_olv_token/token_price
  useEffect(() => {
    setTokenPriceLoading(true);
    const ref = doc(db, 'global_olv_token', 'token_price');
    // Prime with a one-time fetch for immediate display and clearer error surfacing
    (async () => {
      try {
        // Try server read once to bypass cache, then fall back to cache if offline
        let snap;
        try { snap = await getDocFromServer(ref); } catch { snap = await getDoc(ref); }
        const data = (snap.data() as any) || null;
        if (data) {
          const raw = (data.token_price !== undefined ? data.token_price : data.price);
          if (raw !== undefined && raw !== null) {
            let parsed: number | null = null;
            if (typeof raw === 'number' && isFinite(raw)) parsed = raw;
            else if (typeof raw === 'string') {
              const n = parseFloat(raw);
              if (!Number.isNaN(n) && isFinite(n)) parsed = n;
            }
            if (parsed != null) {
              setTokenPriceUsd(parsed);
              setTokenPriceError(null);
              setTokenPriceLoading(false);
            }
          }
        }
      } catch (e: any) {
        console.error('[wallet] initial token price read failed:', e);
      }
    })();

    const unsub = onSnapshot(ref, (snap) => {
      const data = (snap.data() as any) || null;
      if (!data) {
        console.warn('[wallet] token price doc missing at global_olv_token/token_price');
        setTokenPriceError('unavailable');
        setTokenPriceUsd(null);
        setTokenPriceLoading(false);
        return;
      }
      // Prefer the field named "token_price" as requested; fall back to "price" only if needed
      const raw = (data.token_price !== undefined ? data.token_price : data.price);
      if (raw === undefined || raw === null) {
        console.warn('[wallet] neither token_price nor price field is present on token_price document');
        setTokenPriceError('unavailable');
        setTokenPriceUsd(null);
        setTokenPriceLoading(false);
        return;
      }
      let parsed: number | null = null;
      if (typeof raw === 'number' && isFinite(raw)) {
        parsed = raw;
      } else if (typeof raw === 'string') {
        const n = parseFloat(raw);
        if (!Number.isNaN(n) && isFinite(n)) parsed = n;
      }
      if (parsed == null) {
        console.warn('[wallet] token price field is not a valid number:', raw);
        setTokenPriceError('unavailable');
        setTokenPriceUsd(null);
        setTokenPriceLoading(false);
        return;
      }
      setTokenPriceUsd(parsed);
      setTokenPriceError(null);
      setTokenPriceLoading(false);
    }, (err) => {
      console.error('[wallet] failed to read token price:', err);
      setTokenPriceError(err?.message || 'error');
      setTokenPriceLoading(false);
    });
    return () => { try { unsub(); } catch {} };
  }, []);

  const usdFormatter = useMemo(() => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: tokenPriceUsd && tokenPriceUsd < 1 ? 4 : 2,
    maximumFractionDigits: 6
  }), [tokenPriceUsd]);

  // Helper function to format USD balance with proper negative handling
  // Shows -$X.XX instead of $-X.XX for negative values
  // Uses generalized formatUsd for consistent decimal places across the app
  const formatUsdBalance = useCallback((amount: number): string => {
    return formatUsd(amount); // formatUsd already handles negative formatting correctly
  }, []);

  // Add state for payment methods
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<'card' | 'bank' | 'payout' | null>(null);
  const [paymentMethodsExpanded, setPaymentMethodsExpanded] = useState(false);
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [savedCards, setSavedCards] = useState<SavedCard[]>([]);
  const savedCardsRef = useRef<SavedCard[]>([]);
  useEffect(() => { savedCardsRef.current = savedCards; }, [savedCards]);
  // Track whether Stripe API has loaded authoritative payment method data (with real pm_ IDs)
  const stripeSourcedRef = useRef(false);
  const [savedBankAccounts, setSavedBankAccounts] = useState<SavedBankAccount[]>([]);

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    confirmText?: string;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    confirmText: 'delete',
  });

  const showConfirmModal = (title: string, message: string, onConfirm: () => void, confirmText = 'delete') => {
    setConfirmModal({ isOpen: true, title, message, onConfirm, confirmText });
  };

  const closeConfirmModal = () => {
    setConfirmModal(prev => ({ ...prev, isOpen: false }));
  };

  // Connected account state (for receiving payouts)
  const [connectedAccountId, setConnectedAccountId] = useState<string | null>(null);
  const [connectedAccountAvailablePayoutUsd, setConnectedAccountAvailablePayoutUsd] = useState<number | null>(null);
  const [showConnectedAccountModal, setShowConnectedAccountModal] = useState(false);
  const [connectedAccountSubmitting, setConnectedAccountSubmitting] = useState(false);
  const [connectedAccountStep, setConnectedAccountStep] = useState<string>('');
  const [connectedAccountForm, setConnectedAccountForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    dobMonth: '',
    dobDay: '',
    dobYear: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    ssnLast4: '',
    businessType: 'individual' as 'individual' | 'company',
    businessName: '',
    taxId: '',
    accountHolderType: 'individual' as 'individual' | 'company',
    routingNumber: '',
    accountNumber: '',
    confirmAccountNumber: '',
    showSsnDigits: false,
  });
  const [connectedAccountPayoutsEnabled, setConnectedAccountPayoutsEnabled] = useState<boolean | null>(null);
  const [connectedBankInfo, setConnectedBankInfo] = useState<{ bankLast4?: string; bankName?: string } | null>(null);
  // ── Withdraw to bank (task #69) ──────────────────────────────────────────
  // Only purchased/earned pr0 is real money; the quote from
  // requestWalletWithdrawal carries the enforced ceiling + plain-language
  // reasons for the locked portion. The section renders the expectation
  // BEFORE the user commits to anything.
  const [wdQuote, setWdQuote] = useState<any>(null);
  const [wdQuoteError, setWdQuoteError] = useState<boolean>(false);
  const [wdAmount, setWdAmount] = useState('');
  const [wdBusy, setWdBusy] = useState(false);
  const [wdMessage, setWdMessage] = useState<string | null>(null);

  const withdrawFnUrl = `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3'}.cloudfunctions.net/requestWalletWithdrawal`;

  const callWithdrawApi = useCallback(async (body: Record<string, unknown>) => {
    const user = getAuth().currentUser;
    if (!user) throw new Error('not signed in');
    const token = await user.getIdToken();
    const resp = await fetch(withdrawFnUrl, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await resp.json().catch(() => ({}));
    return { ok: resp.ok, data };
  }, [withdrawFnUrl]);

  const loadWdQuote = useCallback(async (): Promise<any | null> => {
    setWdQuoteError(false);
    try {
      const { ok, data } = await callWithdrawApi({ mode: 'quote' });
      if (ok && data?.quote) {
        setWdQuote(data.quote);
        return data.quote;
      }
      setWdQuoteError(true);
    } catch {
      // 2026-08-18 audit: a failed quote used to leave the section on
      // "loading your withdrawable balance…" forever — surface it + retry.
      setWdQuoteError(true);
    }
    return null;
  }, [callWithdrawApi]);

  useEffect(() => { void loadWdQuote(); }, [loadWdQuote]);

  // Stripe onboarding return/refresh (?withdraw=bank-linked | refresh):
  // re-fetch the quote so the button flips to "withdraw to my bank" the
  // moment verification lands, tell the user where they stand, and strip
  // the params so a reload doesn't repeat the notice.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const wdReturn = params.get('withdraw');
    if (!wdReturn) return;
    void (async () => {
      const q = await loadWdQuote();
      if (wdReturn === 'bank-linked') {
        setWdMessage(q?.payoutsEnabled === true
          ? 'bank linked — you can withdraw now.'
          : 'bank linked — verification is still in progress; check back shortly.');
      } else {
        setWdMessage('bank setup was not finished — pick up where you left off anytime.');
      }
      const el = document.querySelector('[data-withdraw-section]');
      if (el && 'scrollIntoView' in el) (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
      params.delete('withdraw');
      const rest = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : ''));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleWithdraw = useCallback(async () => {
    const amount = parseFloat(wdAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setWdMessage('enter how many pr0 to withdraw.');
      return;
    }
    setWdBusy(true);
    setWdMessage(null);
    try {
      const { ok, data } = await callWithdrawApi({ mode: 'request', amountOlv: amount });
      if (data?.needsBankSetup && data?.onboardingUrl) {
        // Bank not linked yet — Stripe onboarding, nothing debited.
        window.location.href = data.onboardingUrl;
        return;
      }
      if (ok && data?.ok) {
        setWdMessage(`on its way — $${Number(data.amountUsd).toFixed(2)} to your bank. arrives per your bank's normal payout timing.`);
        if (data?.quote) setWdQuote(data.quote);
        setWdAmount('');
      } else {
        setWdMessage(data?.error || 'withdrawal failed — your balance is untouched. try again shortly.');
        if (data?.quote) setWdQuote(data.quote);
      }
    } catch {
      setWdMessage('network trouble — nothing was withdrawn. try again shortly.');
    } finally {
      setWdBusy(false);
    }
  }, [wdAmount, callWithdrawApi]);

  // Initial load payment methods from Firestore: users/{uid}/user_wallet/user_wallet
  // Note: Balance is now loaded via WalletSummaryContext from wallet/summary
  const firebaseAuth = getAuth();
  useEffect(() => {
    // Wait for centralized auth check
    if (!authUser) return;

    let canceled = false;
    (async () => {
      try {
        const uid = authUser.uid;
        if (!uid || canceled) return;
        const entry = await getUserWalletEntry(uid);
        if (!entry || canceled) return;

        // Load sequence limit (balance is now from WalletSummaryContext)
        if (typeof entry.sequence_olv_limit === 'number') setSequenceOlvLimit(entry.sequence_olv_limit);
        if (entry.payment_card) {
          const cardLast4 = entry.payment_card.last_four_digits_of_card_number || '';
          const card: SavedCard = {
            id: 'default-card',
            cardNumber: cardLast4 ? `•••• •••• •••• ${cardLast4}` : '',
            expiryDate: entry.payment_card.expiration_date || '',
            cardholderName: entry.payment_card.full_name_on_card || '',
            cardType: entry.payment_card.card_type || 'card',
            isDefault: true
          };
          setSavedCards([card]);
        }
        if (entry.bank_account) {
          const acctLast4 = entry.bank_account.last_four_digits_of_account_number || (entry.bank_account as any).last_fourdigits_of_account_number || '';
          const acct: SavedBankAccount = {
            id: 'default-bank',
            bankName: '',
            accountHolderName: entry.bank_account.full_name_on_account || '',
            accountNumber: acctLast4 ? `••••••${acctLast4}` : '',
            routingNumber: '',
            accountType: entry.bank_account.account_type || 'checking',
            isDefault: true
          };
          setSavedBankAccounts([acct]);
        }
      } catch (e) {
        console.warn('Failed to load user wallet from Firestore:', e);
      }
    })();
    return () => { canceled = true; };
  }, [authUser]);

  // Load connected account status from Firestore users/{uid}
  useEffect(() => {
    if (!authUser) return;

    let canceled = false;
    (async () => {
      try {
        const uid = authUser.uid;
        if (!uid || canceled) return;
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (canceled) return;
        if (userDoc.exists()) {
          const data = userDoc.data();
          if (data?.stripeConnectedAccountId) {
            setConnectedAccountId(data.stripeConnectedAccountId);
            // Load bank info if available
            if (data?.stripeConnectedBankLast4 || data?.stripeConnectedBankName) {
              setConnectedBankInfo({
                bankLast4: data.stripeConnectedBankLast4,
                bankName: data.stripeConnectedBankName
              });
            }
          }
        }
      } catch (e) {
        console.warn('Failed to load connected account from Firestore:', e);
      }
    })();
    return () => { canceled = true; };
  }, [authUser]);

  // Note: Live balance updates are now handled by WalletSummaryContext
  // which listens to wallet/summary Firestore document

  // Live payment method listener so webhook updates reflect immediately
  useEffect(() => {
    // Wait for centralized auth check
    if (!authUser) return;

    let cancelled = false as boolean;
    let unsub: undefined | (() => void);
    (async () => {
      try {
        if (cancelled) return;
        const ref = doc(db, 'users', authUser.uid, 'user_wallet', 'user_wallet');
        unsub = onSnapshot(ref, (snap) => {
          const data = (snap.data() as any) || {};
          // Prefer new arrays
          const savedCardsArr = Array.isArray(data.saved_cards) ? data.saved_cards : [];
          const savedBanksArr = Array.isArray(data.saved_bank_accounts) ? data.saved_bank_accounts : [];
          if (savedCardsArr.length) {
            const cards: SavedCard[] = savedCardsArr.map((c: any, idx: number) => ({
              id: c?.stripe_pm_id || `card-${idx}`,
              cardNumber: c?.last_four_digits_of_card_number ? `•••• •••• •••• ${c.last_four_digits_of_card_number}` : '',
              expiryDate: c?.expiration_date || '',
              cardholderName: c?.full_name_on_card || '',
              cardType: c?.card_type || 'card',
              isDefault: idx === 0
            }));
            // Only update from Firestore if Stripe API hasn't loaded yet,
            // or every card has a valid stripe_pm_id (safe to use)
            const allHaveStripePmId = savedCardsArr.every((c: any) => !!c?.stripe_pm_id);
            if (!stripeSourcedRef.current || allHaveStripePmId) {
              setSavedCards(cards);
            } else {
              // Stripe data loaded but Firestore cards lack PM IDs — only merge cardholder names
              setSavedCards(prev => prev.map(existingCard => {
                const last4 = (existingCard.cardNumber || '').slice(-4);
                const firestoreMatch = cards.find(c => (c.cardNumber || '').slice(-4) === last4);
                if (firestoreMatch && firestoreMatch.cardholderName && !existingCard.cardholderName) {
                  return { ...existingCard, cardholderName: firestoreMatch.cardholderName };
                }
                return existingCard;
              }));
            }
          } else {
            // Fallback to legacy single entry
            const walletEntry = Array.isArray(data.wallet_page) ? data.wallet_page[0] : null;
            if (walletEntry?.payment_card) {
              const cardLast4 = walletEntry.payment_card.last_four_digits_of_card_number || '';
              const card: SavedCard = {
                id: 'default-card',
                cardNumber: cardLast4 ? `•••• •••• •••• ${cardLast4}` : '',
                expiryDate: walletEntry.payment_card.expiration_date || '',
                cardholderName: walletEntry.payment_card.full_name_on_card || '',
                cardType: walletEntry.payment_card.card_type || 'card',
                isDefault: true
              };
              // Don't overwrite Stripe-sourced cards with legacy single-card data
              if (!stripeSourcedRef.current) {
                setSavedCards([card]);
              }
            }
          }

          if (savedBanksArr.length) {
            const banks: SavedBankAccount[] = savedBanksArr.map((b: any, idx: number) => ({
              id: b?.stripe_pm_id || `bank-${idx}`,
              bankName: b?.bank_name || '',
              accountHolderName: b?.full_name_on_account || '',
              accountNumber: b?.last_four_digits_of_account_number ? `••••••${b.last_four_digits_of_account_number}` : '',
              routingNumber: '',
              accountType: b?.account_type || 'checking',
              isDefault: idx === 0
            }));
            const allBanksHaveStripePmId = savedBanksArr.every((b: any) => !!b?.stripe_pm_id);
            if (!stripeSourcedRef.current || allBanksHaveStripePmId) {
              setSavedBankAccounts(banks);
            }
          } else {
            const walletEntry = Array.isArray(data.wallet_page) ? data.wallet_page[0] : null;
            if (walletEntry?.bank_account) {
              const acctLast4 = walletEntry.bank_account.last_four_digits_of_account_number || (walletEntry.bank_account as any).last_fourdigits_of_account_number || '';
              const acct: SavedBankAccount = {
                id: 'default-bank',
                bankName: walletEntry.bank_account.bank_name || '',
                accountHolderName: walletEntry.bank_account.full_name_on_account || '',
                accountNumber: acctLast4 ? `••••••${acctLast4}` : '',
                routingNumber: '',
                accountType: walletEntry.bank_account.account_type || 'checking',
                isDefault: true
              };
              if (!stripeSourcedRef.current) {
                setSavedBankAccounts([acct]);
              }
            }
          }
        });
      } catch {}
    })();
    return () => { cancelled = true; if (typeof unsub === 'function') unsub(); };
  }, [authUser]);
  const anonymousAuthService = useRef(AnonymousAuthService.getInstance());
  // Refresh payment methods directly from Stripe (fallback if webhook hasn't written Firestore yet)
  const refreshPaymentMethods = useCallback(async () => {
    // Use centralized auth state when available, fall back to ensureAuthenticated for callback safety
    if (!authUser) {
      try {
        await anonymousAuthService.current.ensureAuthenticated();
      } catch {
        return; // Auth failed
      }
    }
    try {
      const [cardsResp, banksResp] = await Promise.all([
        stripeApi.listMethods().catch(() => ({ methods: [] as any[] })),
        stripeApi.listBanks().catch(() => ({ methods: [] as any[] }))
      ]);
      const cards = (cardsResp as any)?.methods || [];
      const banks = (banksResp as any)?.methods || [];
      // Pull saved cardholder names from Firestore and merge by last4 so names reliably appear
      let last4ToName: Record<string, string> = {};
      try {
        const user = getAuth().currentUser;
        if (user) {
          const ref = doc(db, 'users', user.uid, 'user_wallet', 'user_wallet');
          const snap = await getDoc(ref);
          const data = (snap.data() as any) || {};
          const saved = Array.isArray(data.saved_cards) ? data.saved_cards : [];
          saved.forEach((c: any) => {
            const k = String(c?.last_four_digits_of_card_number || '').trim();
            if (k) last4ToName[k] = String(c?.full_name_on_card || '').trim();
          });
          // Fallback to legacy wallet_page[0].payment_card if arrays aren't present yet
          if (!saved.length && Array.isArray(data.wallet_page) && data.wallet_page.length) {
            const legacy = data.wallet_page[0]?.payment_card || {};
            const k = String(legacy?.last_four_digits_of_card_number || '').trim();
            if (k) last4ToName[k] = String(legacy?.full_name_on_card || '').trim();
          }
        }
      } catch {}

      const mappedCards: SavedCard[] = cards.map((pm: any) => {
        const last4 = String(pm.last4 || '').trim();
        const expiry = pm.exp_month && pm.exp_year ? `${String(pm.exp_month).padStart(2, '0')} / ${String(pm.exp_year).slice(-2)}` : '';
        const mergedName = (pm.name && String(pm.name).trim()) || (last4 && last4ToName[last4] ? last4ToName[last4] : '');
        return {
          id: String(pm.id),
          cardNumber: last4 ? `•••• •••• •••• ${last4}` : '',
          expiryDate: expiry,
          cardholderName: mergedName,
          cardType: pm.brand || 'card',
          isDefault: Boolean(pm.is_default)
        };
      });
      // Preserve any known cardholder names from existing state by matching last 4 digits
      const mergedCards: SavedCard[] = mappedCards.map((c) => {
        const last4 = (c.cardNumber || '').slice(-4);
        const existing = savedCardsRef.current.find((e) => (e.cardNumber || '').slice(-4) === last4);
        const mergedName = existing?.cardholderName || c.cardholderName || '';
        return { ...c, cardholderName: mergedName };
      });
      setSavedCards(mergedCards);
      stripeSourcedRef.current = true;
      const mappedBanks: SavedBankAccount[] = banks.map((pm: any) => ({
        id: String(pm.id),
        bankName: pm.bank_name || '',
        accountHolderName: '',
        accountNumber: pm.last4 ? `••••••${pm.last4}` : '',
        routingNumber: '',
        accountType: pm.account_type || 'checking',
        isDefault: Boolean(pm.is_default)
      }));
      setSavedBankAccounts(mappedBanks);
    } catch {
      // ignore – UI already has Firestore listeners
    }
  }, [authUser]);

  // Initial refresh on mount in case webhook sync is delayed
  useEffect(() => {
    if (!authUser) return; // Wait for centralized auth
    refreshPaymentMethods().catch(() => {});
  }, [refreshPaymentMethods]);
  
  // Add state for payment method warning modal
  const [showPaymentWarningModal, setShowPaymentWarningModal] = useState(false);
  const [warningModalType, setWarningModalType] = useState<'buy' | 'deposit'>('buy');

  // Add state for payout method warning modal
  const [showPayoutWarningModal, setShowPayoutWarningModal] = useState(false);

  // Notification modal state
  const [notificationModal, setNotificationModal] = useState<{
    isOpen: boolean;
    type: 'success' | 'error' | 'info';
    title: string;
    message: string;
  }>({ isOpen: false, type: 'info', title: '', message: '' });

  const showNotification = useCallback((type: 'success' | 'error' | 'info', title: string, message: string) => {
    setNotificationModal({ isOpen: true, type, title, message });
  }, []);

  const closeNotification = useCallback(() => {
    setNotificationModal(prev => ({ ...prev, isOpen: false }));
  }, []);

  // Add card form state
  const [cardForm, setCardForm] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: '',
    billingAddress: '',
    city: '',
    state: '',
    zipCode: '',
    country: ''
  });

  // Add bank account modal and form state
  const [showAddBankModal, setShowAddBankModal] = useState(false);
  const [bankForm, setBankForm] = useState({
    bankName: '',
    accountHolderName: '',
    accountNumber: '',
    routingNumber: '',
    accountType: 'checking',
    bankAddress: '',
    city: '',
    state: '',
    zipCode: ''
  });
  const [manualBankEntry, setManualBankEntry] = useState(false);
  const [bankLinking, setBankLinking] = useState(false);
  
  // Top-ups go through the SHARED TopUpModal (2026-08-14 unification) — the
  // wallet page no longer carries its own purchase modal/form state.
  // Deposit-modal path removed (2026-08-18 audit): no caller ever opened it —
  // top-ups run through the shared TopUpModal.
  // Legacy withdraw modal state removed (2026-08-18 audit) — the task-#69
  // withdraw card below is the one withdrawal system.
  // Track when the user attempts to enter an amount above balance for UX feedback
  
  // Helper to use live token price for calculations
  const getEffectiveTokenPrice = useCallback(() => {
    return (typeof tokenPriceUsd === 'number' && isFinite(tokenPriceUsd) && tokenPriceUsd > 0)
      ? tokenPriceUsd
      : 0;
  }, [tokenPriceUsd]);
  
  // Stripe client
  const resolveStripePublishableKey = (): string => {
    // 1) Build-time env
    const fromEnv = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
    if (/^pk_(test|live)_/.test(fromEnv) && fromEnv !== 'pk_test_placeholder') return fromEnv;
    // 2) Runtime global override
    try {
      const g = (window as any).__PUBLIC_STRIPE_KEY__;
      if (typeof g === 'string' && /^pk_/.test(g)) return g;
    } catch {}
    // 3) Meta tag
    try {
      const meta = document.querySelector('meta[name="stripe-publishable-key"]') as HTMLMetaElement | null;
      const v = meta?.content || '';
      if (/^pk_/.test(v)) return v;
    } catch {}
    // 4) URL param (supports both ?pk=… and hash fragments); caches to LS
    try {
      const search = window.location.search || '';
      const hashQuery = window.location.hash.includes('?') ? window.location.hash.split('?')[1] : '';
      const parse = (s: string) => new URLSearchParams(s.startsWith('?') ? s : s ? `?${s}` : '');
      const candidate = parse(search).get('pk') || parse(hashQuery).get('pk') || '';
      if (/^pk_/.test(candidate)) {
        try { localStorage.setItem('stripe_pk', candidate); } catch {}
        return candidate;
      }
    } catch {}
    // 5) LocalStorage
    try {
      const ls = localStorage.getItem('stripe_pk') || '';
      if (/^pk_/.test(ls)) return ls;
    } catch {}
    return '';
  };
  const stripePublishableKey = resolveStripePublishableKey();
  const isStripeConfigured = /^pk_(test|live)_/.test(stripePublishableKey) && stripePublishableKey !== 'pk_test_placeholder';
  // Lazy-load Stripe to avoid hot-reload issues
  const stripePromise = useRef<ReturnType<typeof loadStripe> | null>(null);
  const getStripePromise = useCallback(() => {
    if (!stripePromise.current) {
      stripePromise.current = loadStripe(isStripeConfigured ? stripePublishableKey : 'pk_test_placeholder');
    }
    return stripePromise.current;
  }, [isStripeConfigured, stripePublishableKey]);

  // State machine for card initialization
  type InitState =
    | { status: 'idle' }
    | { status: 'validating_config' }
    | { status: 'authenticating' }
    | { status: 'loading_stripe' }
    | { status: 'creating_customer' }
    | { status: 'ready' }
    | { status: 'error'; error: string };

  const [initState, setInitState] = useState<InitState>({ status: 'idle' });
  const isAddCardInitializing = initState.status !== 'idle' && initState.status !== 'ready' && initState.status !== 'error';
  const [initError, setInitError] = useState<string | null>(null);
  const [isSavingCard, setIsSavingCard] = useState(false);

  // Add helper functions for payment methods
  const beginAddCardInit = useCallback(async () => {
    setInitError(null);
    stripeHealthChecker.clearCache(); // Fresh check on every retry
    try {
      // 1. Validate Stripe health FIRST
      setInitState({ status: 'validating_config' });
      if (isStripeConfigured) {
        const health = await stripeHealthChecker.checkHealth(stripePublishableKey);
        if (!health.ok) {
          throw new Error(health.error || 'Stripe configuration invalid');
        }
      }

      // 2. Authenticate user
      setInitState({ status: 'authenticating' });
      // Use centralized authUser state for fast path, fall back to ensureAuth for safety
      let user = authUser || firebaseAuth.currentUser;
      if (!user) {
        try { await anonymousAuthService.current.ensureAuthenticated(); } catch { throw new Error('authentication required'); }
        user = firebaseAuth.currentUser;
      }

      // 3. Load Stripe.js
      setInitState({ status: 'loading_stripe' });
      await getStripePromise(); // ensure Stripe.js is loaded

      // 4. Create customer
      setInitState({ status: 'creating_customer' });
      await stripeApi.createCustomer();

      // Success
      setInitState({ status: 'ready' });
    } catch (e: any) {
      const msg = typeof e?.message === 'string' ? e.message : 'failed to initialize payments';
      setInitError(msg);
      setInitState({ status: 'error', error: msg });
    }
  }, [authUser, firebaseAuth, getStripePromise, isStripeConfigured, stripePublishableKey]);

  const handleAddCard = useCallback(async () => {
    // Open the modal immediately for responsive feedback
    setShowAddCardModal(true);
    // Kick off initialization in background
    beginAddCardInit();
  }, [beginAddCardInit]);

  // Prefetch on hover/focus to reduce perceived latency
  const prewarmAddCard = useCallback(() => {
    // Avoid spamming
    if (isAddCardInitializing) return;
    if (!showAddCardModal) beginAddCardInit();
  }, [isAddCardInitializing, showAddCardModal]);

  const handleCloseModal = () => {
    setShowAddCardModal(false);
    setCardForm({
      cardNumber: '',
      expiryDate: '',
      cvv: '',
      cardholderName: '',
      billingAddress: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    });
  };

  const stripeSubmitRef = useRef<null | (() => Promise<void>)>(null);
  const [isCardSubmitReady, setIsCardSubmitReady] = useState(false);
  const onCardSuccess = useCallback(() => {
    setShowAddCardModal(false);
    // Fresh list right away (2026-08-18 audit) — the row appears with its
    // brand/last4 instead of waiting on the metadata poll.
    void refreshPaymentMethods();
  }, [refreshPaymentMethods]);
  const attachSubmitRef = useCallback((submit: () => Promise<void>) => { stripeSubmitRef.current = submit; }, []);
  const markCardReady = useCallback(() => setIsCardSubmitReady(true), []);

  const handleCardInputChange = (field: string, value: string) => {
    setCardForm(prev => ({ ...prev, [field]: value }));
  };

  type StripeCardSetupProps = { onSuccess: () => void; onMountSubmit?: (submit: () => Promise<void>) => void; configured: boolean; onReady?: () => void; billingName: string; billingAddress?: { line1?: string; city?: string; state?: string; postal_code?: string; country?: string } };
  const StripeCardSetupBase: React.FC<StripeCardSetupProps> = ({ onSuccess, onMountSubmit, configured, onReady, billingName, billingAddress }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const hasSignaledReady = useRef(false);
    const [isFocused, setIsFocused] = useState(false);

    const cardElementOptions = useMemo(() => ({ hidePostalCode: true }), []);

    const onSubmit = async () => {
      if (!configured) {
        setErrorMsg('payments unavailable: stripe publishable key is missing');
        return;
      }
      if (!stripe || !elements) return;
      setLoading(true);
      try { setIsSavingCard(true); } catch {}
      setErrorMsg(null);
      try {
        // Retry logic for setupIntent with exponential backoff (max 2 retries)
        let clientSecret: string | undefined;
        let lastError: string | undefined;

        for (let attempt = 0; attempt < 3; attempt++) {
          const { clientSecret: cs, error } = await stripeApi.setupIntent();
          if (cs) {
            clientSecret = cs;
            break;
          }
          lastError = error;
          // Wait before retry (1s, 2s)
          if (attempt < 2) {
            await new Promise(r => setTimeout(r, Math.pow(2, attempt) * 1000));
          }
        }

        if (!clientSecret) {
          setErrorMsg(lastError || 'failed to start card setup after 3 attempts');
          setLoading(false);
          try { setIsSavingCard(false); } catch {}
          return;
        }
        const card = elements.getElement(CardElement);
        const result = await stripe.confirmCardSetup(clientSecret, {
          payment_method: {
            card: card!,
            // The address fields were collected + validated in the form and
            // then DISCARDED (2026-08-18 audit) — sending them is the honest
            // option and helps issuer authorization rates.
            billing_details: {
              name: billingName || '',
              ...(billingAddress && (billingAddress.line1 || billingAddress.postal_code)
                ? { address: Object.fromEntries(Object.entries(billingAddress).filter(([, v]) => v)) }
                : {}),
            }
          },
          // Provide a return URL to support bank redirects/3DS authentication flows
          return_url: window.location.origin + '/wallet?setup_return=1'
        });
        if (result.error) {
          let userMessage = result.error.message || 'card setup failed';
          let shouldAutoRefresh = false;

          // Enhanced error categorization
          // Configuration mismatch - auto-refresh
          if (result.error.message?.includes('no such setupIntent') ||
              result.error.code === 'resource_missing') {
            console.error('[Wallet] SetupIntent not found - configuration mismatch detected');
            userMessage = 'payment setup failed due to a configuration issue — refreshing in 3 seconds…';
            shouldAutoRefresh = true;
            stripeHealthChecker.clearCache(); // Clear cache to force fresh check on next attempt
          }
          // Card declined
          else if (result.error.code === 'card_declined') {
            userMessage = 'Your card was declined. Please check your card details or try a different card.';
          }
          // Network error
          else if (result.error.type === 'api_connection_error') {
            userMessage = 'Network error. Please check your connection and try again.';
          }
          // Authentication required
          else if (result.error.code === 'authentication_required') {
            userMessage = 'Card authentication required. Please verify your card with your bank.';
          }

          setErrorMsg(userMessage);
          setLoading(false);
          try { setIsSavingCard(false); } catch {}

          // Auto-refresh for configuration errors
          if (shouldAutoRefresh) {
            setTimeout(() => window.location.reload(), 3000);
          }

          return;
        }
        onSuccess();
        showNotification('success', 'card added', 'your card has been added successfully.');
        // Ensure cards tab is active and dropdown is expanded so the user sees the new card immediately
        try { setSelectedPaymentMethod('card'); } catch {}
        try { setPaymentMethodsExpanded(true); } catch {}
        // Immediately refresh from Stripe so the new card appears even before webhook writes Firestore
        try { await refreshPaymentMethods(); } catch {}
        // Optimistically persist basic card info to Firestore in required structure
        try {
          const setup = await stripe.retrieveSetupIntent(clientSecret);
          const pmId = setup.setupIntent?.payment_method as string | undefined;
          let meta: { brand?: string; last4?: string; exp_month?: number; exp_year?: number } | null = null;
          try {
            const resp = await stripeApi.listMethods();
            const list = (resp as any)?.methods || [];
            const match = list.find((m: any) => String(m.id) === String(pmId)) || list[0];
            if (match) meta = { brand: match.brand, last4: match.last4, exp_month: match.exp_month, exp_year: match.exp_year };
          } catch {}
          const user = getAuth().currentUser;
          if (user) {
            const exp = meta?.exp_month && meta?.exp_year ? `${String(meta.exp_month).padStart(2, '0')} / ${String(meta.exp_year).slice(-2)}` : '';
            // Local optimistic UI update so it appears in Saved Cards instantly
            try {
              if (pmId) {
                const optimisticCard: SavedCard = {
                  id: String(pmId),
                  cardNumber: meta?.last4 ? `•••• •••• •••• ${meta.last4}` : '',
                  expiryDate: exp,
                  cardholderName: cardForm.cardholderName || '',
                  cardType: meta?.brand || 'card',
                  isDefault: false
                } as any;
                setSavedCards(prev => {
                  const withoutDup = prev.filter(c => c.id !== optimisticCard.id);
                  return [optimisticCard, ...withoutDup];
                });
              }
            } catch {}
            // Note: Balance is now managed in wallet/summary via WalletSummaryContext
            await saveUserWalletEntry(user.uid, {
              sequence_olv_limit: typeof sequenceOlvLimit === 'number' ? sequenceOlvLimit : undefined,
              payment_card: {
                full_name_on_card: cardForm.cardholderName || '',
                card_type: meta?.brand || 'card',
                last_four_digits_of_card_number: meta?.last4 || '',
                expiration_date: exp
              }
            });

            // If Stripe metadata wasn't ready yet, poll briefly until last4/expiry arrive
            if (pmId && (!meta?.last4 || !(meta?.exp_month && meta?.exp_year))) {
              const deadline = Date.now() + 8000; // up to 8s
              while (Date.now() < deadline) {
                try {
                  const resp2 = await stripeApi.listMethods();
                  const list2 = (resp2 as any)?.methods || [];
                  const found = list2.find((m: any) => String(m.id) === String(pmId));
                  if (found) {
                    const last4 = found.last4 || '';
                    const exp2 = found.exp_month && found.exp_year ? `${String(found.exp_month).padStart(2, '0')} / ${String(found.exp_year).slice(-2)}` : '';
                    if (last4 || exp2) {
                      setSavedCards(prev => prev.map(c => c.id === String(pmId)
                        ? { ...c, cardNumber: last4 ? `•••• •••• •••• ${last4}` : c.cardNumber, expiryDate: exp2 || c.expiryDate, cardType: found.brand || c.cardType }
                        : c));
                      // Backfill Firestore with the details if missing
                      try {
                        await saveUserWalletEntry(user.uid, {
                          payment_card: {
                            full_name_on_card: cardForm.cardholderName || '',
                            card_type: found.brand || meta?.brand || 'card',
                            last_four_digits_of_card_number: last4 || meta?.last4 || '',
                            expiration_date: exp2
                          }
                        });
                      } catch {}
                      break;
                    }
                  }
                } catch {}
                await new Promise(r => setTimeout(r, 600));
              }
            }
          }
        } catch {}
      } catch (e) {
        const msg = (e as any)?.message || 'network error';
        setErrorMsg(msg);
      } finally {
        setLoading(false);
        try { setIsSavingCard(false); } catch {}
      }
    };

    useEffect(() => {
      if (onMountSubmit) {
        onMountSubmit(onSubmit);
      }
      // Immediate readiness signal if hooks are already available
      if (!hasSignaledReady.current && stripe && elements) {
        hasSignaledReady.current = true;
        onReady && onReady();
      }
      // Pre-engine polling fallback: some engines miss CardElement onReady
      const deadlineMs = Date.now() + 5000; // 5s timeout
      const interval = window.setInterval(() => {
        if (hasSignaledReady.current) { window.clearInterval(interval); return; }
        const el = elements?.getElement(CardElement);
        if (stripe && elements && el) {
          hasSignaledReady.current = true;
          window.clearInterval(interval);
          onReady && onReady();
        } else if (Date.now() > deadlineMs) {
          // Give up after timeout but still allow submit if Stripe hooks exist
          if (stripe && elements) {
            hasSignaledReady.current = true;
            onReady && onReady();
          }
          window.clearInterval(interval);
        }
      }, 150);
      return () => window.clearInterval(interval);
    }, [onMountSubmit, stripe, elements, onReady]);

    return (
      <div>
        <CardElement 
          options={cardElementOptions} 
          onReady={() => { if (!hasSignaledReady.current) { hasSignaledReady.current = true; if (onReady) onReady(); } }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
        {loading && <HelperText>processing…</HelperText>}
        {errorMsg ? <ErrorText>{errorMsg}</ErrorText> : null}
        {/* inline button removed; controlled from modal footer */}
      </div>
    );
  };
  const StripeCardSetup = useMemo(() => React.memo(StripeCardSetupBase), []) as React.FC<StripeCardSetupProps>;

  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    } else {
      return v;
    }
  };

  const formatExpiryDate = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return v.substring(0, 2) + '/' + v.substring(2, 4);
    }
    return v;
  };

  const getBrandDisplayName = (cardType: string): string => {
    const brand = cardType.toLowerCase();
    if (brand.includes('visa')) return 'VISA';
    if (brand.includes('master')) return 'MC';
    if (brand.includes('amex') || brand.includes('american')) return 'AMEX';
    if (brand.includes('discover')) return 'DISC';
    if (brand.includes('diners')) return 'DC';
    if (brand.includes('jcb')) return 'JCB';
    return 'CARD';
  };

  const renderCardBrandIcon = (cardType?: string) => {
    const brand = (cardType || '').toLowerCase();
    if (brand.includes('visa')) return <VisaIcon size={48} />;
    if (brand.includes('master')) return <MastercardIcon size={48} />;
    return <CardBrandText cardType={cardType}>{getBrandDisplayName(cardType || '')}</CardBrandText>;
  };

  // Deprecated by Stripe flow
  // handleSaveCard no-op removed (2026-08-18 audit) — card saving runs
  // through the Stripe SetupIntent flow.
  const getCardType = (cardNumber: string) => {
    const number = cardNumber.replace(/\s+/g, '');
    if (number.startsWith('4')) return 'visa';
    if (number.startsWith('5')) return 'mastercard';
    if (number.startsWith('3')) return 'amex';
    return 'card';
  };

  const handleDeleteCard = (cardId: string) => {
    const card = savedCardsRef.current.find(c => c.id === cardId);
    const last4 = card ? (card.cardNumber || '').slice(-4) : '****';

    showConfirmModal(
      'delete card',
      `are you sure you want to remove the card ending in ${last4}? this action cannot be undone.`,
      async () => {
        closeConfirmModal();
        // Optimistic update - remove from UI immediately
        const removedCard = savedCardsRef.current.find(c => c.id === cardId);
        setSavedCards(prev => prev.filter(c => c.id !== cardId));
        // Make API calls in background
        try {
          await stripeApi.detach(cardId);
          // Also remove from Firestore saved_cards by last4
          try {
            const user = getAuth().currentUser;
            if (user && last4 !== '****') await removeSavedCard(user.uid, last4);
          } catch {}
        } catch (e) {
          // Restore card if API call failed
          if (removedCard) {
            setSavedCards(prev => [removedCard, ...prev]);
          }
          showNotification('error', 'delete failed', 'failed to delete card. please try again.');
        }
      },
      'delete card'
    );
  };

  const handleDeleteConnectedAccount = () => {
    const bankInfo = connectedBankInfo;
    const bankDisplay = bankInfo?.bankName
      ? `${bankInfo.bankName} ending in ${bankInfo.bankLast4 || '****'}`
      : 'your payout account';

    showConfirmModal(
      'delete payout account',
      `are you sure you want to remove ${bankDisplay}? you will need to set it up again to receive payouts.`,
      async () => {
        closeConfirmModal();
        // Optimistic update - clear local state immediately
        const previousAccountId = connectedAccountId;
        const previousBankInfo = connectedBankInfo;
        setConnectedAccountId(null);
        setConnectedBankInfo(null);
        // Make API calls in background
        try {
          const user = getAuth().currentUser;
          if (user) {
            // Clear from Firestore
            const userDocRef = doc(db, 'users', user.uid);
            const { updateDoc, deleteField } = await import('firebase/firestore');
            await updateDoc(userDocRef, {
              stripeConnectedAccountId: deleteField(),
              stripeConnectedAccountCreatedAt: deleteField(),
              stripeConnectedAccountStatus: deleteField(),
              stripeConnectedBankLast4: deleteField(),
              stripeConnectedBankName: deleteField(),
            });
          }
        } catch (e) {
          // Restore state if API call failed
          setConnectedAccountId(previousAccountId);
          setConnectedBankInfo(previousBankInfo);
          showNotification('error', 'delete failed', 'failed to delete payout account. please try again.');
        }
      },
      'delete account'
    );
  };

  const maskCardNumber = (cardNumber: string) => {
    const last4 = cardNumber.slice(-4);
    return `•••• •••• •••• ${last4}`;
  };
  
  // Add helper functions for bank account management
  const handleAddBankAccount = () => {
    setShowAddBankModal(true);
  };

  const handleCloseBankModal = () => {
    setShowAddBankModal(false);
    setBankForm({
      bankName: '',
      accountHolderName: '',
      accountNumber: '',
      routingNumber: '',
      accountType: 'checking',
      bankAddress: '',
      city: '',
      state: '',
      zipCode: ''
    });
  };

  const handleBankInputChange = (field: string, value: string) => {
    setBankForm(prev => ({ ...prev, [field]: value }));
  };

  const formatAccountNumber = (value: string) => {
    // Remove non-numeric characters and limit length
    return value.replace(/[^0-9]/g, '').slice(0, 17);
  };

  const formatRoutingNumber = (value: string) => {
    // Remove non-numeric characters and limit to 9 digits
    return value.replace(/[^0-9]/g, '').slice(0, 9);
  };

  const validateRoutingNumber = (routingNumber: string) => {
    // Basic routing number validation (9 digits)
    return routingNumber.length === 9 && /^\d{9}$/.test(routingNumber);
  };

  const isTwoLetterState = (value: string) => /^[A-Z]{2}$/.test(value);
  const isFiveDigitZip = (value: string) => /^\d{5}(-\d{4})?$/.test(value);
  const isCardBillingValid = () => {
    return Boolean(cardForm.cardholderName && cardForm.billingAddress && cardForm.city && isTwoLetterState(cardForm.state) && isFiveDigitZip(cardForm.zipCode));
  };

  const isManualBankFormValid = () => {
    return Boolean(
      bankForm.accountHolderName &&
      bankForm.bankName &&
      bankForm.accountNumber && bankForm.accountNumber.length >= 5 &&
      validateRoutingNumber(bankForm.routingNumber) &&
      bankForm.accountType
    );
  };

  const handleSaveBankAccount = async () => {
    // Launch Stripe Financial Connections to link a bank account securely
    try {
      setBankLinking(true);
      await stripeApi.createCustomer();
      const { clientSecret, error } = await stripeApi.createFinConnSession();
      if (error || !clientSecret) {
        showNotification('error', 'linking failed', error || 'failed to start bank account linking');
      return;
    }
      const stripe = await getStripePromise();
      if (!stripe) { showNotification('error', 'error', 'stripe not available. please try again.'); return; }
      // @ts-ignore: method available at runtime in stripe-js
      const { error: fcError, financialConnectionsSession } = await (stripe as any).collectFinancialConnectionsAccounts({
        clientSecret
      });
      if (fcError) { showNotification('error', 'linking failed', fcError.message || 'linking failed'); return; }
      const accountId: string | undefined = financialConnectionsSession?.accounts?.[0]?.id;
      if (!accountId) { showNotification('error', 'no selection', 'no bank account selected'); return; }
      const attach = await stripeApi.attachFinConnAccount(accountId);
      if ((attach as any).error) { showNotification('error', 'error', (attach as any).error); return; }
      showNotification('success', 'bank linked', 'your bank account has been linked successfully.');
      try { setPaymentMethodsExpanded(true); } catch {}
      handleCloseBankModal();
    } catch (e) {
      showNotification('error', 'linking failed', 'failed to link bank account.');
    } finally {
      setBankLinking(false);
    }
  };

  const handleDeleteBankAccount = (accountId: string) => {
    const account = savedBankAccounts.find(a => a.id === accountId);
    const last4 = account ? (account.accountNumber || '').slice(-4) : '****';
    const bankName = account?.bankName || 'bank account';

    showConfirmModal(
      'delete bank account',
      `are you sure you want to remove ${bankName} ending in ${last4}? this action cannot be undone.`,
      async () => {
        closeConfirmModal();
        // Optimistic update - remove from UI immediately
        const removedAccount = savedBankAccounts.find(a => a.id === accountId);
        setSavedBankAccounts(prev => prev.filter(a => a.id !== accountId));
        // Make API calls in background
        try {
          await stripeApi.detach(accountId);
          try {
            const user = getAuth().currentUser;
            if (user && last4 !== '****') await removeSavedBankAccount(user.uid, last4);
          } catch {}
        } catch (e) {
          // Restore account if API call failed
          if (removedAccount) {
            setSavedBankAccounts(prev => [removedAccount, ...prev]);
          }
          showNotification('error', 'delete failed', 'failed to delete bank account. please try again.');
        }
      },
      'delete account'
    );
  };

  const handleEditBankAccount = (accountId: string) => {
    // In a real implementation, this would open an edit modal
    showNotification('info', 'coming soon', 'edit bank account functionality coming soon.');
  };

  const maskAccountNumber = (accountNumber: string) => {
    const last4 = accountNumber.slice(-4);
    return `••••••${last4}`;
  };

  const getBankIcon = (bankName: string) => {
    // Simple bank name to icon mapping
    const name = bankName.toLowerCase();
    if (name.includes('chase')) return 'chase';
    if (name.includes('bank of america') || name.includes('boa')) return 'boa';
    if (name.includes('wells fargo')) return 'wells';
    if (name.includes('citibank') || name.includes('citi')) return 'citi';
    return 'bank';
  };

  // Add helper functions for token purchase and deposit
  const hasPaymentMethods = () => {
    return savedCards.length > 0 || savedBankAccounts.length > 0;
  };

  const getAllPaymentMethods = (): PaymentMethod[] => {
    const methods: PaymentMethod[] = [];
    savedCards.forEach(card => {
      methods.push({
        id: card.id,
        type: 'card',
        displayName: `${card.cardType.toLowerCase()} ••••${card.cardNumber.slice(-4)}`,
        data: card
      });
    });
    savedBankAccounts.forEach(account => {
      methods.push({
        id: account.id,
        type: 'bank',
        displayName: `${account.bankName} ••••${account.accountNumber.slice(-4)}`,
        data: account
      });
    });
    return methods;
  };

  // URL parameter handling for deep linking from low balance popup
  const [searchParams, setSearchParams] = useSearchParams();
  const urlActionProcessedRef = useRef(false);

  useEffect(() => {
    // Only process once and when payment methods are loaded
    if (urlActionProcessedRef.current) return;

    const action = searchParams.get('action');
    if (!action) return;

    // Wait a tick to ensure payment methods state is settled
    const timeoutId = setTimeout(() => {
      if (urlActionProcessedRef.current) return;
      urlActionProcessedRef.current = true;

      // Clear the param immediately to prevent re-triggering
      searchParams.delete('action');
      setSearchParams(searchParams, { replace: true });

      if (action === 'add-payment') {
        // Same init path as the add-card button (2026-08-18 audit): the
        // direct setShowAddCardModal skipped handleAddCard's form reset +
        // Stripe config pre-check.
        setPaymentMethodsExpanded(true);
        setTimeout(() => { void handleAddCard(); }, 300);
      } else if (action === 'add-tokens') {
        // Shared TopUpModal handles every state itself: expired trials see
        // the trial-ended notice, and users with no saved card get the
        // "use a new card" entry — no pre-gating needed.
        topUp.open('wallet-url-action');
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [searchParams, setSearchParams, savedCards.length, savedBankAccounts.length, topUp]);

  // Every "top up" button opens the SHARED TopUpModal — the same popup the
  // low-balance banners and chat use. It internally handles expired trials
  // (trial-ended notice) and users with no saved card ("use a new card").
  const handleOpenPurchaseModal = () => {
    topUp.open('wallet-page');
  };

  // Handler for deposit funds to bank - requires payout method
  // Open the connected account modal and fetch payouts status if account exists
  const handleOpenConnectedAccountModal = async () => {
    setShowConnectedAccountModal(true);
    if (connectedAccountId) {
      try {
        const status = await stripeApi.getConnectedAccountStatus();
        if (!status.error) {
          setConnectedAccountPayoutsEnabled(status.payoutsEnabled ?? null);
          if (status.availablePayoutUsd !== undefined) {
            setConnectedAccountAvailablePayoutUsd(status.availablePayoutUsd);
          }
        }
      } catch {}
    }
  };

  // From the payout-required warning, open the payout setup modal
  const handleOpenPayoutSetupFromWarning = () => {
    setShowPayoutWarningModal(false);
    handleOpenConnectedAccountModal();
  };

  // From the payment-required warning, open the add card flow directly
  const handleOpenAddCardFromWarning = useCallback(() => {
    setSelectedPaymentMethod('card');
    setShowPaymentWarningModal(false);
    // Scroll to payment methods for context, then open add-card
    setTimeout(() => {
      const paymentSection = document.querySelector('[data-payment-methods]');
      if (paymentSection && 'scrollIntoView' in paymentSection) {
        (paymentSection as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
    handleAddCard();
  }, [handleAddCard]);

  // handleDepositInputChange removed with the dead deposit-modal path (2026-08-18 audit).
  const formatCurrency = (value: string) => {
    const numericValue = value.replace(/[^0-9.]/g, '');
    const parts = numericValue.split('.');
    if (parts.length > 2) {
      return parts[0] + '.' + parts[1];
    }
    if (parts[1] && parts[1].length > 2) {
      return parts[0] + '.' + parts[1].slice(0, 2);
    }
    return numericValue;
  };

  const handleCreateConnectedAccount = async () => {
    try {
      // Validate account numbers match
      if (connectedAccountForm.accountNumber !== connectedAccountForm.confirmAccountNumber) {
        showNotification('error', 'mismatch', 'account numbers do not match. please re-enter.');
        return;
      }

      setConnectedAccountSubmitting(true);
      setConnectedAccountStep('setting up your payout account...');

      const accountData: ConnectedAccountData = {
        email: connectedAccountForm.email,
        firstName: connectedAccountForm.firstName,
        lastName: connectedAccountForm.lastName,
        dobDay: parseInt(connectedAccountForm.dobDay, 10),
        dobMonth: parseInt(connectedAccountForm.dobMonth, 10),
        dobYear: parseInt(connectedAccountForm.dobYear, 10),
        addressLine1: connectedAccountForm.addressLine1,
        addressLine2: connectedAccountForm.addressLine2 || undefined,
        city: connectedAccountForm.city,
        state: connectedAccountForm.state,
        zipCode: connectedAccountForm.zipCode,
        phone: connectedAccountForm.phone,
        ssnLast4: connectedAccountForm.ssnLast4,
        businessType: connectedAccountForm.businessType,
        businessName: connectedAccountForm.businessName || undefined,
        taxId: connectedAccountForm.taxId || undefined,
        accountHolderType: connectedAccountForm.accountHolderType,
        routingNumber: connectedAccountForm.routingNumber,
        accountNumber: connectedAccountForm.accountNumber,
      };

      // Show friendly progress steps during the ~15-20s API call
      const stepTimers = [
        setTimeout(() => setConnectedAccountStep('confirming your information...'), 3000),
        setTimeout(() => setConnectedAccountStep('connecting your bank account...'), 7000),
        setTimeout(() => setConnectedAccountStep('almost done — just a few more seconds...'), 12000),
      ];

      const result = await stripeApi.createConnectedAccount(accountData);
      stepTimers.forEach(clearTimeout);

      if (result.error) {
        showNotification('error', 'failed', result.error);
        return;
      }

      if (result.accountId) {
        setConnectedAccountId(result.accountId);
        setConnectedBankInfo({ bankLast4: result.bankLast4, bankName: result.bankName });
        setConnectedAccountPayoutsEnabled(result.payoutsEnabled ?? null);
        setShowConnectedAccountModal(false);
        setConnectedAccountForm({
          email: '',
          firstName: '',
          lastName: '',
          dobMonth: '',
          dobDay: '',
          dobYear: '',
          addressLine1: '',
          addressLine2: '',
          city: '',
          state: '',
          zipCode: '',
          phone: '',
          ssnLast4: '',
          businessType: 'individual',
          businessName: '',
          taxId: '',
          accountHolderType: 'individual',
          routingNumber: '',
          accountNumber: '',
          confirmAccountNumber: '',
          showSsnDigits: false,
        });
        if (result.payoutsEnabled) {
          showNotification('success', 'payout account active', 'your payout account is verified and ready. you can withdraw funds now.');
        } else {
          showNotification('info', 'account created — pending verification', 'your identity is being verified. you\'ll receive an email when payouts are enabled, usually within a few minutes.');
        }
        try { setPaymentMethodsExpanded(true); } catch {}
      }
    } catch (e: any) {
      showNotification('error', 'error', e.message || 'failed to create payout account');
    } finally {
      setConnectedAccountSubmitting(false);
      setConnectedAccountStep('');
    }
  };

  const calculateTokensFromUSD = (usdAmount: string) => {
    const usd = parseFloat(usdAmount);
    if (isNaN(usd) || usd <= 0) return 0;
    const price = getEffectiveTokenPrice();
    return price > 0 ? Math.floor(usd / price) : 0;
  };

  const calculateUSDFromTokens = (tokenAmount: string) => {
    const tokens = parseInt(tokenAmount);
    if (isNaN(tokens) || tokens <= 0) return 0;
    const price = getEffectiveTokenPrice();
    const usd = price > 0 ? tokens * price : 0;
    return Math.round(usd * 100) / 100; // cent rounding
  };

  // Add handler for closing payment warning modal
  const handleClosePaymentWarningModal = () => {
    setShowPaymentWarningModal(false);
  };

  // Add handler for closing payout warning modal
  const handleClosePayoutWarningModal = () => {
    setShowPayoutWarningModal(false);
  };

  // Close on Escape for any open modal
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (notificationModal.isOpen) closeNotification();
        else if (showAddCardModal) handleCloseModal();
        else if (showAddBankModal) handleCloseBankModal();
        else if (showPaymentWarningModal) handleClosePaymentWarningModal();
        else if (showPayoutWarningModal) handleClosePayoutWarningModal();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showAddCardModal, showAddBankModal, showPaymentWarningModal, showPayoutWarningModal, notificationModal.isOpen, closeNotification]);

  return (
    <>
      <SeoHead
        tabTitle="wallet"
        title="Wallet | prest0"
        description="manage balances, payment methods, and purchases in your prest0 wallet"
      />
      {/* Add proper viewport meta tag for responsive design */}
      <header>
        {/* Screen reader only title */}
        <ScreenReaderOnly>Wallet Page</ScreenReaderOnly>
      </header>
      
      <WalletContainer>
        {/* Email verification banner */}
        {authUser && !authUser.emailVerified && !emailBannerDismissed && (
          <NotificationBanner variant="info">
            <BannerIcon>✉️</BannerIcon>
            <BannerBody>
              <BannerTitle>verify your email</BannerTitle>
              <span>check your inbox for a verification link. some features require a verified email.</span>
              <BannerActions>
                <BannerButton
                  onClick={handleResendVerification}
                  disabled={verificationSending || verificationSent}
                >
                  {verificationSent ? 'sent!' : verificationSending ? 'sending…' : 'resend email'}
                </BannerButton>
              </BannerActions>
            </BannerBody>
            <BannerDismiss
              onClick={() => {
                setEmailBannerDismissed(true);
                try { localStorage.setItem('emailBannerDismissed', '1'); } catch {}
              }}
              aria-label="dismiss"
            >✕</BannerDismiss>
          </NotificationBanner>
        )}

        {/* Low balance warning banner */}
        {!lowBalanceDismissed && lowBalanceState.shouldWarn && (
          <NotificationBanner variant="warning">
            <BannerIcon>⚠️</BannerIcon>
            <BannerBody>
              <BannerTitle>
                {lowBalanceState.isCriticalBalance ? 'critical: low balance' : 'low balance'}
              </BannerTitle>
              <span>
                your balance is {formatUsd(lowBalanceState.balanceUsd)}.{' '}
                {lowBalanceState.isCriticalBalance
                  ? 'add funds to continue using ai features.'
                  : 'consider topping up to avoid interruptions.'}
              </span>
              <BannerActions>
                <BannerButton onClick={handleOpenPurchaseModal}>top up</BannerButton>
              </BannerActions>
            </BannerBody>
            <BannerDismiss onClick={() => setLowBalanceDismissed(true)} aria-label="dismiss">✕</BannerDismiss>
          </NotificationBanner>
        )}

        {/* Profile-completion card removed (2026-08-17 owner directive): no
            portal surface may solicit information inline — future gathering
            happens via popups/in-chat, and only for information genuinely
            not on file. The organization questions still live permanently on
            the profile page's organization section. */}

        {/* Usage stats summary tiles removed per Miles — balance info is in the main wallet card below */}

        {/* Live spend vs the organization's pooled limits (2026-08-14) —
            renders nothing when no limits are configured or the viewer
            lacks the pool-limits permission. */}
        <WalletSpendMeters />

        {/* Balance Section */}
        <BalanceCard>
          {/* Org wallets name their organization (2026-08-21 owner directive):
              "[org name]'s wallet balance", s-ending names taking the bare
              apostrophe; the generic label covers only the beat before the
              profile name loads. Personal stays "your wallet balance". */}
          <BalanceHeader>
            {isAccountWalletDisplayed
              ? `${orgWalletName ? possessiveName(orgWalletName) : 'this workspace\u2019s'} wallet balance`
              : 'your wallet balance'}
          </BalanceHeader>
          
          {/* pr0 work units display (2026-08-13; label corrected 2026-08-21 —
              it is pr0 with the logo-zero mark, never "PRO"): the primary
              balance renders as WU = USD value / Standard Rate ($2.18). The
              wallet's internal denomination (OLV at the live rate) and the
              derived usdBalance plumbing are UNCHANGED — display only. */}
          <BalanceRow>
            <BalanceLabel><Pr0Unit /> work units</BalanceLabel>
            <BalanceValue>
              {isBalanceLoading ? (
                <BalanceShimmer />
              ) : (
                <>
                  {formatWu(usdToWu(usdBalance))} <Pr0Unit />
                </>
              )}
            </BalanceValue>
          </BalanceRow>

          <BalanceRow>
            <BalanceLabel>USD value</BalanceLabel>
            <BalanceValue>
              {isBalanceLoading ? <BalanceShimmer /> : formatUsdBalance(usdBalance)}
            </BalanceValue>
          </BalanceRow>

          <BalanceActions>
            <SecondaryActionButton onClick={handleOpenPurchaseModal}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="9"/>
                <path d="M8 12h8"/>
                <path d="M12 16V8"/>
              </svg>
              top up <Pr0Unit />
            </SecondaryActionButton>
            <ActionButton
              onClick={() => {
                // One withdrawal system (2026-08-18 audit): this button now
                // leads to the task-#69 withdraw card below — quote first,
                // provenance ceiling, honest locked-credit reasons.
                const el = document.querySelector('[data-withdraw-section]');
                if (el && 'scrollIntoView' in el) {
                  (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
              </svg>
              withdraw to bank
            </ActionButton>
          </BalanceActions>
          
          {/* Transaction history is shown as a standalone section below payment methods */}
        </BalanceCard>
        
        {/* Standard Rate Section (static — 1 WU = $2.18) */}
        <TokenRateTile />

        {/* "Fund this workspace's wallet" (personal→org transfer) REMOVED
            (2026-08-21 owner directive): there must be no way to move pr0
            between wallets/organizations from the app. Stripe top-ups via
            the shared TopUpModal remain the funding path. */}

        {/* ── Withdraw to bank (task #69; promoted out of the accordion,
            2026-08-18 audit — money out deserves its own card, not a row
            buried in a collapsed section). The quote is the contract:
            withdrawable now (purchased/earned only), the locked remainder,
            and WHY each locked class stays. */}
        <PaymentCard data-withdraw-section>
          <SavedCardsSection>
            <PaymentSectionLabel>withdraw to bank</PaymentSectionLabel>
            {isAccountWalletDisplayed && (
              <div style={{ fontSize: '0.76rem', color: '#857F73', margin: '-0.2rem 0 0.5rem', lineHeight: 1.5 }}>
                withdrawals draw on your PERSONAL withdrawable balance — not this workspace&apos;s wallet.
              </div>
            )}
            {wdQuoteError ? (
              <div style={{ fontSize: '0.84rem', color: '#9B2C2C', lineHeight: 1.6, display: 'flex', gap: '0.8rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <span>couldn&apos;t load your withdrawable balance.</span>
                <button
                  type="button"
                  onClick={() => { void loadWdQuote(); }}
                  style={{
                    padding: '0.35rem 0.9rem', fontSize: '0.8rem', fontWeight: 600,
                    color: '#4A463E', background: '#FCFBF7', border: '1px solid #E4E0D5',
                    borderRadius: '0.5rem', cursor: 'pointer', textTransform: 'lowercase',
                  }}
                >
                  try again
                </button>
              </div>
            ) : !wdQuote ? (
              <div style={{ fontSize: '0.84rem', color: '#857F73', lineHeight: 1.6 }}>
                loading your withdrawable balance…
              </div>
            ) : (
              <div style={{ fontSize: '0.86rem', color: '#4A463E', lineHeight: 1.65 }}>
                <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
                  <span>
                    withdrawable now:{' '}
                    <strong>{Number(wdQuote.withdrawableOlv || 0).toLocaleString(undefined, { maximumFractionDigits: 3 })} pr0</strong>
                    {wdQuote.withdrawableUsd != null && (
                      <> (≈ ${Number(wdQuote.withdrawableUsd).toFixed(2)})</>
                    )}
                  </span>
                  {Number(wdQuote.lockedOlv) > 0 && (
                    <span style={{ color: '#857F73' }}>
                      stays on the platform: {Number(wdQuote.lockedOlv).toLocaleString(undefined, { maximumFractionDigits: 3 })} pr0
                    </span>
                  )}
                </div>
                {Number(wdQuote.lockedOlv) > 0 && (
                  <div style={{ fontSize: '0.76rem', color: '#857F73', marginBottom: '0.8rem', lineHeight: 1.6 }}>
                    only credit you paid full price for — or earned from work others paid you
                    for — can be withdrawn. trial, promotional, team-bonus, and committed-plan
                    credit is for putting Prest0n to work, and stays here.
                    {Array.isArray(wdQuote.lockedReasons) && wdQuote.lockedReasons.length > 0 && (
                      <ul style={{ margin: '0.45rem 0 0', paddingLeft: '1.1rem' }}>
                        {wdQuote.lockedReasons.map((reason: string) => (
                          <li key={reason}>{reason}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                {Number(wdQuote.withdrawableOlv) > 0 ? (
                  <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                      type="number"
                      min="0"
                      step="0.001"
                      placeholder="pr0 to withdraw"
                      value={wdAmount}
                      onChange={(e) => setWdAmount(e.target.value)}
                      style={{
                        padding: '0.55rem 0.75rem', fontSize: '0.88rem', width: '11rem',
                        border: '1px solid #E4E0D5', borderRadius: '0.6rem', background: '#FCFBF7',
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleWithdraw}
                      disabled={wdBusy}
                      style={{
                        padding: '0.55rem 1.2rem', fontSize: '0.86rem', fontWeight: 600,
                        color: '#fff', background: 'linear-gradient(135deg, var(--brand-gold), #A8863C)',
                        border: 'none', borderRadius: '0.6rem', cursor: wdBusy ? 'wait' : 'pointer',
                        textTransform: 'lowercase',
                      }}
                    >
                      {wdBusy
                        ? 'working…'
                        : wdQuote.hasBankAccount && wdQuote.payoutsEnabled
                          ? 'withdraw to my bank'
                          : 'link my bank & withdraw'}
                    </button>
                  </div>
                ) : (
                  <div style={{ fontSize: '0.8rem', color: '#857F73' }}>
                    nothing to withdraw yet — top-ups you pay full price for (and, soon, work
                    others pay you for) build your withdrawable balance.
                  </div>
                )}
                {wdMessage && (
                  <div style={{ marginTop: '0.6rem', fontSize: '0.82rem', color: wdMessage.startsWith('on its way') || wdMessage.startsWith('bank linked — you can') ? '#067647' : '#9B2C2C' }}>
                    {wdMessage}
                  </div>
                )}
              </div>
            )}
          </SavedCardsSection>
        </PaymentCard>
        
        {/* Payment Methods Section */}
        <PaymentCard data-payment-methods>
          <DropdownHeader
            $isExpanded={paymentMethodsExpanded}
            onClick={() => setPaymentMethodsExpanded(!paymentMethodsExpanded)}
            aria-expanded={paymentMethodsExpanded}
            aria-controls="payment-methods-content"
          >
            <DropdownHeaderContent>
              payment & payout methods
              <SecurityBadge>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <circle cx="12" cy="16" r="1"></circle>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
                secure
              </SecurityBadge>
            </DropdownHeaderContent>
            <DropdownChevron $isExpanded={paymentMethodsExpanded}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </DropdownChevron>
          </DropdownHeader>

          <DropdownContent $isExpanded={paymentMethodsExpanded} id="payment-methods-content">
          {/* Saved Cards Section - Only show when cards exist */}
          {savedCards.length > 0 && (
            <SavedCardsSection>
              <PaymentSectionLabel>
                {isAccountWalletDisplayed ? 'your personal cards' : 'saved cards'} ({savedCards.length})
              </PaymentSectionLabel>
              {isAccountWalletDisplayed && (
                <div style={{ fontSize: '0.76rem', color: '#857F73', margin: '-0.2rem 0 0.5rem', lineHeight: 1.5 }}>
                  cards stay on your personal account — the displayed balance above is this workspace&apos;s.
                </div>
              )}
              <SavedCardsList>
                {savedCards.map((card) => (
                  <SavedCardItem key={card.id}>
                    <CardInfo>
                      <CardIcon cardType={card.cardType}>
                        {renderCardBrandIcon(card.cardType)}
                      </CardIcon>
                      <CardDetails>
                        <CardNumber>
                          {maskCardNumber(card.cardNumber)}
                          {card.isDefault && (
                            <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', fontWeight: 700, color: '#A8863C', letterSpacing: '0.02em' }}>
                              default
                            </span>
                          )}
                        </CardNumber>
                        <CardExpiry>expires {card.expiryDate}</CardExpiry>
                        {card.cardholderName ? <CardholderName>{card.cardholderName}</CardholderName> : null}
                        {/* 2026-08-18 audit: the default is what the top-up and
                            hire modals preselect — changing it finally has a
                            surface (the endpoint always existed). */}
                        {!card.isDefault && card.id.startsWith('pm_') && (
                          <button
                            type="button"
                            onClick={async (e) => {
                              e.stopPropagation();
                              const r = await stripeApi.setDefaultPaymentMethod(card.id);
                              if ((r as any)?.ok) { void refreshPaymentMethods(); }
                              else { showNotification('error', 'could not set default', String((r as any)?.error || 'try again shortly.').toLowerCase()); }
                            }}
                            style={{
                              marginTop: '0.3rem', alignSelf: 'flex-start', padding: '0.15rem 0.55rem',
                              fontSize: '0.72rem', fontWeight: 600, color: '#4A463E',
                              background: '#FCFBF7', border: '1px solid #E4E0D5', borderRadius: '0.45rem',
                              cursor: 'pointer', textTransform: 'lowercase',
                            }}
                          >
                            make default
                          </button>
                        )}
                        {/* Task #80: cards are personal — sharing with an
                            organization is the owner's explicit per-org choice,
                            made right here (including the moment a new card
                            lands in this list). */}
                        {card.id.startsWith('pm_') ? (
                          <CardOrgSharing paymentMethodId={card.id} />
                        ) : (
                          <div style={{ fontSize: '0.72rem', color: '#B0AA9E', marginTop: '0.45rem' }}>
                            sharing becomes available when this card is next re-saved.
                          </div>
                        )}
                      </CardDetails>
                    </CardInfo>
                    <CardActions>
                      <CardActionButton
                        className="delete"
                        onClick={(e) => { e.stopPropagation(); handleDeleteCard(card.id); }}
                        title="Delete card"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3,6 5,6 21,6"></polyline>
                          <path d="M19,6V20a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6M8,6V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      </CardActionButton>
                    </CardActions>
                  </SavedCardItem>
                ))}
              </SavedCardsList>
            </SavedCardsSection>
          )}

          {/* Withdraw to bank moved to its own top-level card (2026-08-18 audit) */}

          {/* Saved Payout Method Section - Always visible when verified */}
          {connectedAccountId && (
            <SavedCardsSection>
              <PaymentSectionLabel>default payout method</PaymentSectionLabel>
              <SavedCardsList>
                <SavedCardItem>
                  <CardInfo>
                    <CardIcon cardType="payout" style={{ background: 'var(--brand-gold)' }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 21h18"/>
                        <path d="M3 10h18"/>
                        <path d="M5 6l7-3 7 3"/>
                        <path d="M4 10v11"/>
                        <path d="M20 10v11"/>
                        <path d="M8 14v3"/>
                        <path d="M12 14v3"/>
                        <path d="M16 14v3"/>
                      </svg>
                    </CardIcon>
                    <CardDetails>
                      <CardNumber>
                        {connectedBankInfo?.bankName || 'bank account'} ••••{connectedBankInfo?.bankLast4 || '****'}
                      </CardNumber>
                    </CardDetails>
                  </CardInfo>
                  <CardActions>
                    <CardActionButton
                      className="delete"
                      onClick={(e) => { e.stopPropagation(); handleDeleteConnectedAccount(); }}
                      title="Delete payout account"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3,6 5,6 21,6"></polyline>
                        <path d="M19,6V20a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6M8,6V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6"></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                      </svg>
                    </CardActionButton>
                  </CardActions>
                </SavedCardItem>
              </SavedCardsList>
            </SavedCardsSection>
          )}

          <PaymentMethodGrid>
            <PaymentMethodCard
              isSelected={selectedPaymentMethod === 'card'}
              onClick={() => {
                setSelectedPaymentMethod('card');
                // If no cards are saved, streamline by opening the add card modal immediately
                if (savedCards.length === 0) {
                  handleAddCard();
                }
              }}
            >
              <PaymentMethodHeader>
                <PaymentMethodTitle>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                    <line x1="1" y1="10" x2="23" y2="10"></line>
                  </svg>
                  credit/debit cards
                </PaymentMethodTitle>
              </PaymentMethodHeader>
              <AddNewButton onClick={handleAddCard} onMouseEnter={prewarmAddCard} onFocus={prewarmAddCard}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                add new card
              </AddNewButton>
            </PaymentMethodCard>

            <PaymentMethodCard
              isSelected={false}
              onClick={() => handleOpenConnectedAccountModal()}
            >
              <PaymentMethodHeader>
                <PaymentMethodTitle>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                  </svg>
                  default payout
                </PaymentMethodTitle>
              </PaymentMethodHeader>
              <AddNewButton onClick={(e) => { e.stopPropagation(); handleOpenConnectedAccountModal(); }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {connectedAccountId ? (
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  ) : (
                    <>
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </>
                  )}
                </svg>
                {connectedAccountId ? 'manage payout' : 'set up payout'}
              </AddNewButton>
            </PaymentMethodCard>
          </PaymentMethodGrid>
          </DropdownContent>
        </PaymentCard>

        {/* Spending & usage limits — owner-only guardrails (moved here from the
            Settings page 2026-08). Sits with the other money controls, above the
            history sections. Owner-gated, fail-closed while RBAC resolves. */}
        {authUser && !rbacLoading && viewerIsWorkspaceOwner && (
          <UsageLimitsCard uid={authUser.uid} />
        )}

        {/* Earnings History - Platform payouts, monetization revenue, and sales */}
        <EarningsHistory showSummary={true} />

        {/* Transactions History - Payments and deposits (money in/out) */}
        <TransactionsHistory showSummary={true} />

        {/* Expenses History - AI compute costs, subscriptions, purchases, and fees */}
        <ExpensesHistory showSummary={true} />

        {/* Usage History removed (parity plan Phase 0, 2026-08-02): the
            section read users/{uid}/wallet/summary/usage, a collection with
            NO writer anywhere (client or server) — it always rendered empty.
            Per-model usage lives on ExpensesHistory rows (generations[]),
            fed by the server's per-generation ledger.

            ORG-USAGE-2026: the usage-analytics dashboard that used to sit here
            moved to the dedicated Activity page (/activity), which is
            permission-gated and scoped per member. The Wallet page is now
            purely about money: balance, payment methods, earnings,
            transactions, and expenses. */}
        <WalletSection>
          <SectionTitle>usage &amp; activity</SectionTitle>
          <ActivityPointer>
            <p>
              detailed usage analytics — Work Units, spend by system, and a full request history —
              now live on the activity page.
            </p>
            <ActivityLink to="/activity">view activity →</ActivityLink>
          </ActivityPointer>
        </WalletSection>

        {/* Add Card Modal */}
        <AddCardModal isOpen={showAddCardModal} onClick={handleCloseModal} role="dialog" aria-modal="true" aria-labelledby="add-card-title">
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle id="add-card-title">add new card</ModalTitle>
              <CloseButton onClick={handleCloseModal}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </CloseButton>
            </ModalHeader>
            {(isAddCardInitializing) && <ModalProgressBar />}

            {/* Progress indicators based on initialization state */}
            {initState.status === 'validating_config' && <ProgressStep>validating configuration...</ProgressStep>}
            {initState.status === 'authenticating' && <ProgressStep>authenticating...</ProgressStep>}
            {initState.status === 'loading_stripe' && <ProgressStep>loading secure form...</ProgressStep>}
            {initState.status === 'creating_customer' && <ProgressStep>preparing payment setup...</ProgressStep>}

            {/* Enhanced error display */}
            {initState.status === 'error' && (
              <ErrorBox>
                <ErrorTitle>initialization failed</ErrorTitle>
                <ErrorMessage>{initState.error}</ErrorMessage>
              </ErrorBox>
            )}

            <ModalSubtitle>securely add a credit or debit card</ModalSubtitle>

            <FormGroup>
              <FormLabel>cardholder name</FormLabel>
              <FormInput
                type="text"
                placeholder="john doe"
                value={cardForm.cardholderName}
                onChange={(e) => handleCardInputChange('cardholderName', e.target.value)}
                aria-label="cardholder name"
              />
              <HelperText>enter the name exactly as it appears on your card</HelperText>
            </FormGroup>

            <FormGroup>
              <FormLabel>card details</FormLabel>
              <StripeField>
                {(isAddCardInitializing || !isCardSubmitReady) && (
                  <FieldOverlay aria-live="polite" aria-busy={true}>
                    <SkeletonStack>
                      <SkeletonLine style={{ width: '55%' }} />
                      <SkeletonLine style={{ width: '35%' }} />
                    </SkeletonStack>
                    <Spinner />
                  </FieldOverlay>
                )}
                <Elements stripe={getStripePromise()} options={useMemo(() => ({ appearance: { variables: { fontFamily: 'inherit' } } }), [])}>
                  <StripeCardSetup
                    configured={isStripeConfigured}
                    onSuccess={onCardSuccess}
                    onMountSubmit={attachSubmitRef}
                    onReady={markCardReady}
                    billingName={cardForm.cardholderName}
                    billingAddress={{
                      line1: cardForm.billingAddress || undefined,
                      city: cardForm.city || undefined,
                      state: cardForm.state || undefined,
                      postal_code: cardForm.zipCode || undefined,
                      country: cardForm.country || undefined,
                    }}
                  />
                </Elements>
                {/* When saving, overlay the field with clear feedback */}
                {isSavingCard && (
                  <SavingOverlay role="status" aria-live="polite">
                    <Spinner />
                    <span style={{ color: '#374151', fontSize: '0.9rem' }}>saving card…</span>
                  </SavingOverlay>
                )}
              </StripeField>
              {initError ? <ErrorText>{initError}</ErrorText> : null}
              <AcceptedBrands>accepted: visa • mastercard • amex</AcceptedBrands>
            </FormGroup>

            <FormGroup>
              <FormLabel>billing address</FormLabel>
              <FormInput
                type="text"
                placeholder="123 main street"
                value={cardForm.billingAddress}
                onChange={(e) => handleCardInputChange('billingAddress', e.target.value)}
                aria-label="billing address"
              />
              <HelperText>use the address associated with your card</HelperText>
            </FormGroup>

            <FormRow>
              <FormGroup>
                <FormLabel>city</FormLabel>
                <FormInput
                  type="text"
                  placeholder="new york"
                  value={cardForm.city}
                  onChange={(e) => handleCardInputChange('city', e.target.value)}
                  aria-label="city"
                />
              </FormGroup>
              <FormGroup>
                <FormLabel>state</FormLabel>
                <FormInput
                  type="text"
                  placeholder="ny"
                  value={cardForm.state}
                  onChange={(e) => handleCardInputChange('state', e.target.value.toUpperCase())}
                  maxLength={2}
                  aria-label="state"
                />
                {!isTwoLetterState(cardForm.state) && cardForm.state && (
                  <ErrorText>enter a 2-letter state code</ErrorText>
                )}
              </FormGroup>
              <FormGroup>
                <FormLabel>zip code</FormLabel>
                <FormInput
                  type="text"
                  placeholder="10001"
                  value={cardForm.zipCode}
                  onChange={(e) => handleCardInputChange('zipCode', e.target.value)}
                  aria-label="zip code"
                />
                {!isFiveDigitZip(cardForm.zipCode) && cardForm.zipCode && (
                  <ErrorText>enter a valid zip code</ErrorText>
                )}
              </FormGroup>
            </FormRow>

            <FormGroup>
              <FormLabel>country</FormLabel>
              <FormSelect
                value={cardForm.country || 'US'}
                onChange={(e) => handleCardInputChange('country', e.target.value)}
                aria-label="country"
              >
                <option value="US">united states</option>
                <option value="CA">canada</option>
                <option value="GB">united kingdom</option>
                <option value="AU">australia</option>
                <option value="DE">germany</option>
              </FormSelect>
            </FormGroup>

            <SecurityInfo>
              <SecurityInfoText>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <circle cx="12" cy="16" r="1"></circle>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
                <PoweredByStripe>security powered by stripe</PoweredByStripe>
              </SecurityInfoText>
            </SecurityInfo>

            <ModalActions>
              <ModalButton variant="secondary" onClick={handleCloseModal}>
                cancel
              </ModalButton>
              <ModalButton
                variant="primary"
                onClick={() => {
                  if (stripeSubmitRef.current) { stripeSubmitRef.current(); }
                  else { showNotification('info', 'please wait', 'initializing payments… please wait a moment and try again.'); }
                }}
                disabled={!isCardSubmitReady || isAddCardInitializing}
              >
                {isAddCardInitializing || !isCardSubmitReady ? 'initializing…' : (isSavingCard ? 'saving…' : 'save card')}
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </AddCardModal>

        {/* Add Bank Account Modal */}
        <AddCardModal isOpen={showAddBankModal} onClick={handleCloseBankModal} role="dialog" aria-modal="true" aria-labelledby="add-bank-title">
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle id="add-bank-title">add bank account</ModalTitle>
              <CloseButton onClick={handleCloseBankModal}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </CloseButton>
            </ModalHeader>
            <ModalSubtitle>link your bank securely for faster deposits and lower fees. recommended for most users.</ModalSubtitle>

            <OptionCard>
              <OptionTitle>
                link via secure bank connection <InlineBadge>recommended</InlineBadge>
              </OptionTitle>
              <OptionDescription>
                connect directly to your bank using encrypted financial connections. we never see your credentials.
              </OptionDescription>
              <ModalButton variant="primary" onClick={handleSaveBankAccount} disabled={bankLinking}>
                {bankLinking ? 'connecting…' : 'link bank securely'}
              </ModalButton>
            </OptionCard>

            <CollapsibleToggle onClick={() => setManualBankEntry(v => !v)} aria-expanded={manualBankEntry} aria-controls="manual-bank-entry">
              {manualBankEntry ? 'hide manual entry' : 'enter details manually (advanced)'}
            </CollapsibleToggle>

            <CollapsibleContent id="manual-bank-entry" open={manualBankEntry}>
            <FormGroup>
              <FormLabel>account holder name *</FormLabel>
              <FormInput
                type="text"
                placeholder="john doe"
                value={bankForm.accountHolderName}
                onChange={(e) => handleBankInputChange('accountHolderName', e.target.value)}
              />
              <HelperText>full legal name on the bank account</HelperText>
            </FormGroup>

            <FormGroup>
              <FormLabel>bank name *</FormLabel>
              <FormInput
                type="text"
                placeholder="chase bank"
                value={bankForm.bankName}
                onChange={(e) => handleBankInputChange('bankName', e.target.value)}
              />
            </FormGroup>

            <FormRow>
              <FormGroup>
                <FormLabel>account number *</FormLabel>
                <FormInput
                  type="text"
                  placeholder="123456789"
                  value={bankForm.accountNumber}
                  onChange={(e) => handleBankInputChange('accountNumber', formatAccountNumber(e.target.value))}
                  maxLength={17}
                />
                {bankForm.accountNumber && bankForm.accountNumber.length < 5 && (
                  <ErrorText>enter a valid account number</ErrorText>
                )}
              </FormGroup>
              <FormGroup>
                <FormLabel>routing number *</FormLabel>
                <FormInput
                  type="text"
                  placeholder="021000021"
                  value={bankForm.routingNumber}
                  onChange={(e) => handleBankInputChange('routingNumber', formatRoutingNumber(e.target.value))}
                  maxLength={9}
                />
                {bankForm.routingNumber && !validateRoutingNumber(bankForm.routingNumber) && (
                  <ErrorText>routing number must be 9 digits</ErrorText>
                )}
              </FormGroup>
            </FormRow>

            <FormGroup>
              <FormLabel>account type *</FormLabel>
              <FormSelect
                value={bankForm.accountType}
                onChange={(e) => handleBankInputChange('accountType', e.target.value)}
              >
                <option value="checking">checking account</option>
                <option value="savings">savings account</option>
                <option value="business">business account</option>
              </FormSelect>
            </FormGroup>

            <FormGroup>
              <FormLabel>bank address (optional)</FormLabel>
              <FormInput
                type="text"
                placeholder="123 bank street"
                value={bankForm.bankAddress}
                onChange={(e) => handleBankInputChange('bankAddress', e.target.value)}
              />
            </FormGroup>

            <FormRow>
              <FormGroup>
                <FormLabel>city</FormLabel>
                <FormInput
                  type="text"
                  placeholder="new york"
                  value={bankForm.city}
                  onChange={(e) => handleBankInputChange('city', e.target.value)}
                />
              </FormGroup>
              <FormGroup>
                <FormLabel>state</FormLabel>
                <FormInput
                  type="text"
                  placeholder="ny"
                  value={bankForm.state}
                  onChange={(e) => handleBankInputChange('state', e.target.value.toUpperCase())}
                  maxLength={2}
                />
                {!isTwoLetterState(bankForm.state) && bankForm.state && (
                  <ErrorText>enter a 2-letter state code</ErrorText>
                )}
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <FormLabel>zip code</FormLabel>
                <FormInput
                  type="text"
                  placeholder="10001"
                  value={bankForm.zipCode}
                  onChange={(e) => handleBankInputChange('zipCode', e.target.value)}
                />
                {!isFiveDigitZip(bankForm.zipCode) && bankForm.zipCode && (
                  <ErrorText>enter a valid zip code</ErrorText>
                )}
              </FormGroup>
              <div></div>
            </FormRow>
            </CollapsibleContent>

            <SecurityInfo>
              <SecurityInfoText>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <circle cx="12" cy="16" r="1"></circle>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
                your bank account information is encrypted and securely stored. we use bank-level security measures to protect your data.
              </SecurityInfoText>
              <PoweredByStripe>powered by stripe</PoweredByStripe>
            </SecurityInfo>

            <ModalActions>
              <ModalButton variant="secondary" onClick={handleCloseBankModal}>
                cancel
              </ModalButton>
              <ModalButton
                variant="primary"
                onClick={() => { if (!manualBankEntry || isManualBankFormValid()) { handleSaveBankAccount(); } else { showNotification('error', 'incomplete form', 'please complete all required fields.'); } }}
                disabled={bankLinking || (manualBankEntry && !isManualBankFormValid())}
              >
                add bank account
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </AddCardModal>


        {/* Deposit Funds Modal */}
        {/* deposit modal removed (2026-08-18 audit) — dead path, no opener */}

        {/* Withdraw to Bank Modal (transfers from wallet to bank account) */}

        {/* Payment Method Warning Modal */}
        <AddCardModal isOpen={showPaymentWarningModal} onClick={handleClosePaymentWarningModal} role="dialog" aria-modal="true" aria-labelledby="payment-required-title">
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle id="payment-required-title">payment method required</ModalTitle>
              <CloseButton onClick={handleClosePaymentWarningModal}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </CloseButton>
            </ModalHeader>

            <WarningContainer>
              <WarningAlert>
                <WarningIcon viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                  <line x1="12" y1="9" x2="12" y2="13"></line>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </WarningIcon>
                <WarningContent>
                  <WarningText>
                    {warningModalType === 'buy'
                      ? 'you need to add a payment method before topping up Work Units.'
                      : 'you need to add a payment method before depositing funds.'}
                  </WarningText>
                  <WarningText>
                    please add a credit/debit card or bank account to continue.
                  </WarningText>
                </WarningContent>
              </WarningAlert>

              <PaymentMethodsSection>
                <PaymentMethodsTitle>available payment methods:</PaymentMethodsTitle>
                <PaymentMethodsList>
                  <PaymentMethodOption
                    title="recommended"
                    role="button"
                    tabIndex={0}
                    onClick={handleOpenAddCardFromWarning}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { handleOpenAddCardFromWarning(); } }}
                    style={{ cursor: 'pointer' }}
                  >
                    <PaymentMethodIcon viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                      <line x1="1" y1="10" x2="23" y2="10"></line>
                    </PaymentMethodIcon>
                    <PaymentMethodName>
                      credit/debit cards <InlineBadge>recommended</InlineBadge>
                    </PaymentMethodName>
                  </PaymentMethodOption>
                </PaymentMethodsList>
              </PaymentMethodsSection>
            </WarningContainer>

            <ModalActions>
              <ModalButton variant="secondary" onClick={handleClosePaymentWarningModal}>
                close
              </ModalButton>
              <ModalButton variant="primary" onClick={handleOpenAddCardFromWarning}>
                add payment method
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </AddCardModal>

        {/* Trial Ended — token purchases blocked */}
        {/* trial-blocked modal removed (2026-08-18 audit) — dead since the shared TopUpModal took over the notice */}

        {/* Payout Method Warning Modal */}
        <AddCardModal isOpen={showPayoutWarningModal} onClick={handleClosePayoutWarningModal} role="dialog" aria-modal="true" aria-labelledby="payout-required-title">
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle id="payout-required-title">payout method required</ModalTitle>
              <CloseButton onClick={handleClosePayoutWarningModal}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </CloseButton>
            </ModalHeader>

            <WarningContainer>
              <WarningAlert>
                <WarningIcon viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                  <line x1="12" y1="9" x2="12" y2="13"></line>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </WarningIcon>
                <WarningContent>
                  <WarningText>
                    you need to set up a payout method before withdrawing funds to your bank account.
                  </WarningText>
                  <WarningText>
                    please add a bank account to continue.
                  </WarningText>
                </WarningContent>
              </WarningAlert>

              <PaymentMethodsSection>
                <PaymentMethodsTitle>set up payout method:</PaymentMethodsTitle>
                <PaymentMethodsList>
                  <PaymentMethodOption
                    role="button"
                    tabIndex={0}
                    onClick={handleOpenPayoutSetupFromWarning}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { handleOpenPayoutSetupFromWarning(); } }}
                    style={{ cursor: 'pointer' }}
                  >
                    <PaymentMethodIcon viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 21h18"/>
                      <path d="M3 10h18"/>
                      <path d="M5 6l7-3 7 3"/>
                      <path d="M4 10v11"/>
                      <path d="M20 10v11"/>
                      <path d="M8 14v3"/>
                      <path d="M12 14v3"/>
                      <path d="M16 14v3"/>
                    </PaymentMethodIcon>
                    <PaymentMethodName>
                      bank account
                    </PaymentMethodName>
                  </PaymentMethodOption>
                </PaymentMethodsList>
              </PaymentMethodsSection>
            </WarningContainer>

            <ModalActions>
              <ModalButton variant="secondary" onClick={handleClosePayoutWarningModal}>
                close
              </ModalButton>
              <ModalButton variant="primary" onClick={handleOpenPayoutSetupFromWarning}>
                set up payout
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </AddCardModal>

        {/* Notification Modal */}
        <AddCardModal isOpen={notificationModal.isOpen} onClick={closeNotification} role="dialog" aria-modal="true" aria-labelledby="notification-title">
          <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <ModalHeader>
              <ModalTitle id="notification-title">{notificationModal.title}</ModalTitle>
              <CloseButton onClick={closeNotification}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </CloseButton>
            </ModalHeader>

            {notificationModal.type === 'success' && (
              <SuccessAlert>
                <SuccessIcon viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </SuccessIcon>
                <SuccessText>{notificationModal.message}</SuccessText>
              </SuccessAlert>
            )}

            {notificationModal.type === 'error' && (
              <ErrorAlert>
                <ErrorIcon viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </ErrorIcon>
                <NotificationErrorText>{notificationModal.message}</NotificationErrorText>
              </ErrorAlert>
            )}

            {notificationModal.type === 'info' && (
              <InfoAlert>
                <InfoIcon viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </InfoIcon>
                <InfoText>{notificationModal.message}</InfoText>
              </InfoAlert>
            )}

            <ModalActions style={{ justifyContent: 'center' }}>
              <ModalButton variant="primary" onClick={closeNotification}>
                OK
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </AddCardModal>

        {/* Connected Account Setup Modal */}
        <AddCardModal isOpen={showConnectedAccountModal} onClick={() => setShowConnectedAccountModal(false)} role="dialog" aria-modal="true" aria-labelledby="connected-account-title">
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle id="connected-account-title">
                {connectedAccountId ? 'payout account' : 'set up payout'}
              </ModalTitle>
              <CloseButton onClick={() => setShowConnectedAccountModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </CloseButton>
            </ModalHeader>

            {connectedAccountId ? (
              <>
                <ModalSubtitle>
                  {connectedAccountPayoutsEnabled
                    ? 'your payout account is active and ready to receive withdrawals.'
                    : 'your payout account is linked, but stripe is still verifying it before payouts can begin.'}
                </ModalSubtitle>

                <div style={{ padding: '1rem', background: '#f5f5f5', borderRadius: '8px', marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="var(--brand-gold)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 24, height: 24 }}>
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                        <polyline points="22 4 12 14.01 9 11.01"/>
                      </svg>
                      <span style={{ fontWeight: 600, color: '#333' }}>bank account linked</span>
                    </div>
                    <span style={{
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      padding: '0.25rem 0.6rem',
                      borderRadius: '999px',
                      background: connectedAccountPayoutsEnabled ? '#ecfdf3' : '#fff7ed',
                      color: connectedAccountPayoutsEnabled ? '#027a48' : '#b45309',
                      border: connectedAccountPayoutsEnabled ? '1px solid #abefc6' : '1px solid #fed7aa'
                    }}>
                      {connectedAccountPayoutsEnabled ? '✓ payouts active' : '⏳ pending verification'}
                    </span>
                  </div>
                  <div style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.75rem' }}>
                    {connectedBankInfo?.bankName && <div>{connectedBankInfo.bankName}</div>}
                    <div>Account ending in ••••{connectedBankInfo?.bankLast4 || '****'}</div>
                  </div>
                  {!connectedAccountPayoutsEnabled && (
                    <HelperText style={{ margin: 0 }}>
                      your payout account is still being reviewed. once verification is complete, withdrawals will work automatically.
                    </HelperText>
                  )}
                </div>

                <HelperText style={{ marginBottom: '1rem' }}>
                  payouts will be deposited to this bank account within 2-3 business days once stripe enables payouts.
                </HelperText>

                <ModalActions>
                  {!connectedAccountPayoutsEnabled && (
                    <ModalButton
                      variant="secondary"
                      onClick={async () => {
                        try {
                          const status = await stripeApi.getConnectedAccountStatus();
                          if (!status.error) {
                            setConnectedAccountPayoutsEnabled(status.payoutsEnabled ?? null);
                            if (status.payoutsEnabled) {
                              showNotification('success', 'payouts active', 'verification is complete. withdrawals are now enabled.');
                            } else {
                              showNotification('info', 'still pending', 'verification is still in progress. please check back shortly.');
                            }
                          }
                        } catch {
                          showNotification('error', 'check failed', 'unable to refresh payout status right now.');
                        }
                      }}
                    >
                      check status
                    </ModalButton>
                  )}
                  <ModalButton variant="secondary" onClick={() => setShowConnectedAccountModal(false)}>
                    close
                  </ModalButton>
                </ModalActions>
              </>
            ) : (
              <>
                <ModalSubtitle>enter your information to receive payouts. this is required for compliance.</ModalSubtitle>

                <div style={{
                  padding: '1rem', background: 'linear-gradient(135deg, rgba(198, 167, 94, 0.08) 0%, rgba(198, 167, 94, 0.04) 100%)',
                  borderRadius: '10px', marginBottom: '1.5rem', border: '1px solid rgba(198, 167, 94, 0.25)',
                }}>
                  <div style={{ fontWeight: 600, color: '#333', fontSize: '0.95rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="var(--brand-gold)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
                      <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                    what to expect
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '1.25rem', color: '#555', fontSize: '0.85rem', lineHeight: 1.7 }}>
                    <li>after you submit, setup takes about <strong>15-20 seconds</strong> — please don't close this page</li>
                    <li>your identity will be verified, which usually completes within <strong>a few minutes</strong></li>
                    <li>you'll receive a <strong>confirmation email</strong> once verification is complete</li>
                    <li>once verified, you can withdraw funds directly to your bank account</li>
                  </ul>
                </div>

                <PurchaseModeToggle style={{ marginBottom: '2rem' }}>
                  <ModeButton
                    active={connectedAccountForm.businessType === 'individual'}
                    onClick={() => setConnectedAccountForm(prev => ({ ...prev, businessType: 'individual', accountHolderType: 'individual' }))}
                  >
                    personal account
                  </ModeButton>
                  <ModeButton
                    active={connectedAccountForm.businessType === 'company'}
                    onClick={() => setConnectedAccountForm(prev => ({ ...prev, businessType: 'company', accountHolderType: 'company' }))}
                  >
                    business account
                  </ModeButton>
                </PurchaseModeToggle>

                <FormLabel style={{ fontSize: '1.1rem', marginBottom: '1rem', borderBottom: '1px solid var(--brand-line)', paddingBottom: '0.5rem' }}>
                  {connectedAccountForm.businessType === 'company' ? "representative's personal info" : "personal info"}
                </FormLabel>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <FormGroup>
                    <FormLabel>first name *</FormLabel>
                    <FormInput
                      type="text"
                      placeholder="Sarah"
                      value={connectedAccountForm.firstName}
                      onChange={(e) => setConnectedAccountForm(prev => ({ ...prev, firstName: e.target.value }))}
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>last name *</FormLabel>
                    <FormInput
                      type="text"
                      placeholder="Johnson"
                      value={connectedAccountForm.lastName}
                      onChange={(e) => setConnectedAccountForm(prev => ({ ...prev, lastName: e.target.value }))}
                    />
                  </FormGroup>
                </div>

                <FormRow>
                  <FormGroup>
                    <FormLabel>email *</FormLabel>
                    <FormInput
                      type="email"
                      placeholder="sarah@example.com"
                      value={connectedAccountForm.email}
                      onChange={(e) => setConnectedAccountForm(prev => ({ ...prev, email: e.target.value }))}
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>phone number *</FormLabel>
                    <FormInput
                      type="tel"
                      placeholder="(555) 555-5555"
                      value={connectedAccountForm.phone}
                      onChange={(e) => {
                        let val = e.target.value.replace(/\D/g, '');
                        if (val.length > 10) val = val.slice(0, 10);
                        let formatted = val;
                        if (val.length > 6) formatted = `(${val.slice(0, 3)}) ${val.slice(3, 6)}-${val.slice(6)}`;
                        else if (val.length > 3) formatted = `(${val.slice(0, 3)}) ${val.slice(3)}`;
                        else if (val.length > 0) formatted = `(${val}`;
                        setConnectedAccountForm(prev => ({ ...prev, phone: formatted }));
                      }}
                    />
                    {connectedAccountForm.phone && connectedAccountForm.phone.replace(/\D/g, '').length !== 10 && (
                      <ErrorText>enter a 10-digit phone number</ErrorText>
                    )}
                  </FormGroup>
                </FormRow>

                <FormGroup>
                  <FormLabel>date of birth *</FormLabel>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
                    <FormInput
                      type="text"
                      placeholder="MM"
                      maxLength={2}
                      value={connectedAccountForm.dobMonth}
                      onChange={(e) => setConnectedAccountForm(prev => ({ ...prev, dobMonth: e.target.value.replace(/\D/g, '').slice(0, 2) }))}
                    />
                    <FormInput
                      type="text"
                      placeholder="DD"
                      maxLength={2}
                      value={connectedAccountForm.dobDay}
                      onChange={(e) => setConnectedAccountForm(prev => ({ ...prev, dobDay: e.target.value.replace(/\D/g, '').slice(0, 2) }))}
                    />
                    <FormInput
                      type="text"
                      placeholder="YYYY"
                      maxLength={4}
                      value={connectedAccountForm.dobYear}
                      onChange={(e) => setConnectedAccountForm(prev => ({ ...prev, dobYear: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                    />
                  </div>
                </FormGroup>

                <FormGroup>
                  <FormLabel>address line 1 *</FormLabel>
                  <FormInput
                    type="text"
                    placeholder="123 Market St"
                    value={connectedAccountForm.addressLine1}
                    onChange={(e) => setConnectedAccountForm(prev => ({ ...prev, addressLine1: e.target.value }))}
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel>address line 2</FormLabel>
                  <FormInput
                    type="text"
                    placeholder="Apt 4B (optional)"
                    value={connectedAccountForm.addressLine2}
                    onChange={(e) => setConnectedAccountForm(prev => ({ ...prev, addressLine2: e.target.value }))}
                  />
                </FormGroup>

                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '0.5rem' }}>
                  <FormGroup>
                    <FormLabel>city *</FormLabel>
                    <FormInput
                      type="text"
                      placeholder="San Francisco"
                      value={connectedAccountForm.city}
                      onChange={(e) => setConnectedAccountForm(prev => ({ ...prev, city: e.target.value }))}
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>state *</FormLabel>
                    <FormInput
                      type="text"
                      placeholder="CA"
                      maxLength={2}
                      value={connectedAccountForm.state}
                      onChange={(e) => setConnectedAccountForm(prev => ({ ...prev, state: e.target.value.toUpperCase().slice(0, 2) }))}
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>zip *</FormLabel>
                    <FormInput
                      type="text"
                      placeholder="94102"
                      maxLength={5}
                      value={connectedAccountForm.zipCode}
                      onChange={(e) => setConnectedAccountForm(prev => ({ ...prev, zipCode: e.target.value.replace(/\D/g, '').slice(0, 5) }))}
                    />
                  </FormGroup>
                </div>

                {connectedAccountForm.businessType === 'company' && (
                  <>
                    <FormLabel style={{ fontSize: '1.1rem', marginTop: '1.5rem', marginBottom: '1rem', borderBottom: '1px solid var(--brand-line)', paddingBottom: '0.5rem' }}>
                      business info
                    </FormLabel>
                    <FormGroup>
                      <FormLabel>business name *</FormLabel>
                      <FormInput
                        type="text"
                        placeholder="Acme Corp"
                        value={connectedAccountForm.businessName}
                        onChange={(e) => setConnectedAccountForm(prev => ({ ...prev, businessName: e.target.value }))}
                      />
                    </FormGroup>
                    <FormGroup>
                      <FormLabel>employer identification number (EIN) *</FormLabel>
                      <PasswordInput
                        inputComponent={FormInput}
                        placeholder="12-3456789"
                        maxLength={10}
                        value={connectedAccountForm.taxId}
                        onChange={(e) => {
                          let val = e.target.value.replace(/\D/g, '');
                          if (val.length > 9) val = val.slice(0, 9);
                          let formatted = val;
                          if (val.length > 2) formatted = `${val.slice(0, 2)}-${val.slice(2)}`;
                          setConnectedAccountForm(prev => ({ ...prev, taxId: formatted }));
                        }}
                      />
                    </FormGroup>
                  </>
                )}

                <FormLabel style={{ fontSize: '1.1rem', marginTop: '1.5rem', marginBottom: '1rem', borderBottom: '1px solid var(--brand-line)', paddingBottom: '0.5rem' }}>
                  identity verification
                </FormLabel>
                <HelperText style={{ marginBottom: '1rem' }}>
                  required by our payment processor for identity verification. we never store your full ssn.
                </HelperText>
                
                <FormGroup>
                  <FormLabel>{connectedAccountForm.businessType === 'company' ? "representative's ssn last 4 digits *" : "ssn last 4 digits *"}</FormLabel>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                    <FormInput
                      type={connectedAccountForm.showSsnDigits ? 'text' : 'password'}
                      placeholder="••••"
                      maxLength={4}
                      style={{ width: '120px' }}
                      value={connectedAccountForm.ssnLast4}
                      onChange={(e) => setConnectedAccountForm(prev => ({ ...prev, ssnLast4: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                    />
                    <ModalButton 
                      type="button" 
                      variant="secondary"
                      onClick={() => setConnectedAccountForm(prev => ({ ...prev, showSsnDigits: !prev.showSsnDigits }))}
                    >
                      {connectedAccountForm.showSsnDigits ? 'hide' : 'show'}
                    </ModalButton>
                  </div>
                </FormGroup>

                <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e0e0e0' }}>
                  <FormLabel style={{ fontSize: '1.1rem', marginBottom: '1rem', borderBottom: '1px solid var(--brand-line)', paddingBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18, marginRight: 8 }}>
                      <line x1="3" y1="6" x2="21" y2="6"></line>
                      <line x1="3" y1="12" x2="21" y2="12"></line>
                      <line x1="3" y1="18" x2="21" y2="18"></line>
                    </svg>
                    bank account for payouts
                  </FormLabel>

                  <FormGroup>
                    <FormLabel>account holder type *</FormLabel>
                    <FormSelect
                      value={connectedAccountForm.accountHolderType}
                      onChange={(e) => setConnectedAccountForm(prev => ({ ...prev, accountHolderType: e.target.value as 'individual' | 'company' }))}
                    >
                      <option value="individual">personal/individual</option>
                      <option value="company">business/company</option>
                    </FormSelect>
                  </FormGroup>

                  <FormGroup>
                    <FormLabel>routing number *</FormLabel>
                    <FormInput
                      type="text"
                      placeholder="123456789"
                      maxLength={9}
                      value={connectedAccountForm.routingNumber}
                      onChange={(e) => setConnectedAccountForm(prev => ({ ...prev, routingNumber: e.target.value.replace(/\D/g, '').slice(0, 9) }))}
                    />
                  </FormGroup>

                  <FormGroup>
                    <FormLabel>account number *</FormLabel>
                    <PasswordInput
                      inputComponent={FormInput}
                      placeholder="••••••••••"
                      maxLength={17}
                      value={connectedAccountForm.accountNumber}
                      onChange={(e) => setConnectedAccountForm(prev => ({ ...prev, accountNumber: e.target.value.replace(/\D/g, '').slice(0, 17) }))}
                    />
                  </FormGroup>

                  <FormGroup>
                    <FormLabel>confirm account number *</FormLabel>
                    <FormInput
                      type="text"
                      placeholder="re-enter account number"
                      maxLength={17}
                      value={connectedAccountForm.confirmAccountNumber}
                      onChange={(e) => setConnectedAccountForm(prev => ({ ...prev, confirmAccountNumber: e.target.value.replace(/\D/g, '').slice(0, 17) }))}
                    />
                    {connectedAccountForm.accountNumber && connectedAccountForm.confirmAccountNumber && connectedAccountForm.accountNumber !== connectedAccountForm.confirmAccountNumber && (
                      <ErrorText>account numbers do not match</ErrorText>
                    )}
                  </FormGroup>
                </div>

                <SecurityInfo>
                  <SecurityInfoText>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                      <circle cx="12" cy="16" r="1"></circle>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                    </svg>
                    your information is encrypted and securely transmitted to stripe. we never store your full bank account number.
                  </SecurityInfoText>
                </SecurityInfo>

                <HelperText>
                  by continuing, you agree to stripe's <a href="https://stripe.com/connect-account/legal" target="_blank" rel="noopener noreferrer">connected account agreement</a>.
                </HelperText>

                <ModalActions>
                  <ModalButton variant="secondary" onClick={() => setShowConnectedAccountModal(false)}>
                    cancel
                  </ModalButton>
                  <ModalButton
                    variant="primary"
                    onClick={handleCreateConnectedAccount}
                    disabled={
                      connectedAccountSubmitting || 
                      !connectedAccountForm.firstName || 
                      !connectedAccountForm.lastName || 
                      !connectedAccountForm.email || 
                      !connectedAccountForm.phone ||
                      connectedAccountForm.phone.replace(/\D/g, '').length !== 10 ||
                      !connectedAccountForm.dobMonth || 
                      !connectedAccountForm.dobDay || 
                      !connectedAccountForm.dobYear || 
                      !connectedAccountForm.addressLine1 || 
                      !connectedAccountForm.city || 
                      !connectedAccountForm.state || 
                      !connectedAccountForm.zipCode || 
                      (!connectedAccountForm.ssnLast4 || connectedAccountForm.ssnLast4.length !== 4) ||
                      (connectedAccountForm.businessType === 'company' && (!connectedAccountForm.businessName || !connectedAccountForm.taxId)) ||
                      !connectedAccountForm.routingNumber || 
                      !connectedAccountForm.accountNumber || 
                      !connectedAccountForm.confirmAccountNumber || 
                      connectedAccountForm.accountNumber !== connectedAccountForm.confirmAccountNumber
                    }
                  >
                    {connectedAccountSubmitting ? 'setting up...' : 'set up payout'}
                  </ModalButton>
                </ModalActions>

                {connectedAccountSubmitting && connectedAccountStep && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                    padding: '1rem', background: 'rgba(198, 167, 94, 0.1)',
                    borderRadius: '8px', marginTop: '1rem', border: '1px solid rgba(198, 167, 94, 0.3)',
                  }} role="status" aria-live="polite">
                    <div style={{
                      width: '20px', height: '20px', border: '2px solid var(--brand-gold)',
                      borderTop: '2px solid transparent', borderRadius: '50%',
                      animation: 'spin 1s linear infinite', flexShrink: 0,
                    }} />
                    <span style={{ color: '#333', fontSize: '0.9rem', fontWeight: 500 }}>
                      {connectedAccountStep}
                    </span>
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                  </div>
                )}
              </>
            )}
          </ModalContent>
        </AddCardModal>

        {/* Confirmation Modal */}
        <ConfirmModal isOpen={confirmModal.isOpen} onClick={closeConfirmModal}>
          <ConfirmModalContent onClick={(e) => e.stopPropagation()}>
            <ConfirmIconWrapper variant="danger">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3,6 5,6 21,6"></polyline>
                <path d="M19,6V20a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6M8,6V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6"></path>
                <line x1="10" y1="11" x2="10" y2="17"></line>
                <line x1="14" y1="11" x2="14" y2="17"></line>
              </svg>
            </ConfirmIconWrapper>
            <ConfirmTitle>{confirmModal.title}</ConfirmTitle>
            <ConfirmMessage>{confirmModal.message}</ConfirmMessage>
            <ConfirmActions>
              <ConfirmButton variant="cancel" onClick={closeConfirmModal}>
                cancel
              </ConfirmButton>
              <ConfirmButton variant="danger" onClick={confirmModal.onConfirm}>
                {confirmModal.confirmText || 'delete'}
              </ConfirmButton>
            </ConfirmActions>
          </ConfirmModalContent>
        </ConfirmModal>

        {/* 2026-08-13 PRO Work Units migration: the old "monetize your AI
            agent" / utility-token marketing sections were replaced with a
            single sober explainer of the Work Units denomination. */}
        <WalletSection>
          <SectionTitle>Work Units</SectionTitle>
          <SectionContent>
            <p>
              Work Units — one unit of finished work. Your balance tops up at the Standard
              Rate (${STANDARD_RATE_USD_PER_WU.toFixed(2)} / <Pr0Unit />); committed Prest0n hires include{' '}
              {MONTHLY_WU_PER_PRESTON.toLocaleString()} <Pr0Unit /> per month.
            </p>
          </SectionContent>
        </WalletSection>
      </WalletContainer>
      
      <FooterWrapper>
        <GlobalFooter />
      </FooterWrapper>
    </>
  );
};

// ORG-USAGE-2026: organizations can decide which members see billing.
// FAIL-OPEN by construction (see hooks/usageVisibility.ts): the wall below only
// appears once RBAC has resolved cleanly AND the account has been upgraded to
// the usage-visibility model AND the member genuinely lacks `billing.view`.
// While loading, on any error, and for every account that predates the feature,
// the wallet renders exactly as it always has.
const WalletAccessWall = styled.div`
  max-width: 640px;
  margin: 4rem auto;
  padding: 0 1.5rem;
  text-align: center;

  h2 {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1.25rem;
    font-weight: 500;
    color: var(--brand-black);
    margin: 0 0 0.75rem;
    text-transform: lowercase;
  }

  p {
    color: #666;
    font-size: 0.9375rem;
    line-height: 1.6;
    margin: 0;
  }
`;

// Wrapper component that provides WalletDataProvider context
const Wallet: React.FC = () => {
  const { canSeeWallet, ready } = useUsageVisibility();

  if (ready && !canSeeWallet) {
    return (
      <>
        <WalletAccessWall>
          <h2>billing isn't available for your role</h2>
          <p>
            your organization limits who can see billing and wallet details. ask your
            account admin for permission to view billing (the “view billing” permission).
          </p>
        </WalletAccessWall>
        <FooterWrapper>
          <GlobalFooter />
        </FooterWrapper>
      </>
    );
  }

  return (
    <WalletDataProvider>
      <WalletContent />
    </WalletDataProvider>
  );
};

export default Wallet; 